// Flat key -> string per language. Parameters are interpolated as {name}.
type Dict = Record<string, string>;

const en: Dict = {
  "appName": "dotSupport",

  "nav.dashboard": "Dashboard",
  "nav.tickets": "Tickets",
  "nav.contacts": "Contacts",
  "nav.knowledge": "Knowledge base",
  "nav.reports": "AI reports",
  "reports.title": "AI reports",
  "reports.intro": "Describe in your own words which tickets you want to export and what should be in the file. AI turns your description into a filter, shows you the match, and only then generates the file.",
  "reports.queryLabel": "What do you want to export?",
  "reports.queryHelp": "Example: 'Summarise the replies to my campaign email with subject containing XYZ in Dutch and ZYX in French. Include the email address, the full reply thread and an AI summary of each reply.'",
  "reports.queryPlaceholder": "Describe the tickets you want to export…",
  "reports.formatLabel": "Output format",
  "reports.analyseButton": "Analyse",
  "reports.planning": "Analysing…",
  "reports.previewTitle": "Preview",
  "reports.explanationLabel": "Interpretation",
  "reports.matchCount": "Matched tickets",
  "reports.cappedAt": "capped at",
  "reports.columns": "Columns",
  "reports.colField": "field",
  "reports.colAi": "AI",
  "reports.estimatedCost": "Estimated AI cost",
  "reports.aiCalls": "AI calls",
  "reports.sampleTitle": "Sample (first 3)",
  "reports.colSubject": "Subject",
  "reports.colEmail": "Email",
  "reports.colStatus": "Status",
  "reports.generateAndDownload": "Generate & download",
  "reports.generating": "Generating…",
  "reports.largeJobWarning": "This may take a moment — summaries are batched, but the model still has to process every ticket.",
  "reports.progress": "Processed {done} of {total} tickets…",
  "reports.cancelled": "Report generation was cancelled.",
  "reports.historyTitle": "Stored reports",
  "reports.historyEmpty": "No reports stored yet — the first report you generate will appear here.",
  "reports.histWhen": "When",
  "reports.histFormat": "Format",
  "reports.histRows": "Rows",
  "reports.histQuery": "Request",
  "reports.histStatus": "Status",
  "reports.download": "Download",
  "reports.deleteConfirmTitle": "Delete report?",
  "reports.deleteConfirmBody": "Permanently remove '{name}'? The file cannot be recovered.",
  "reports.jobStartFailed": "Could not start the report job — the backend may still be deploying. Try again in a minute.",
  "reports.jobStatusUnreadable": "The server stopped answering the status check. Refresh the page and try again.",
  "reports.noAccessTitle": "No access",
  "reports.noAccessBody": "Your role doesn't have AI access. Ask an admin to grant it if you need to run AI reports.",
  "nav.settings": "Settings",

  "language.label": "Language",
  "intake.name": "Name",
  "intake.email": "Email",
  "intake.subject": "Subject",
  "intake.description": "Description",
  "intake.submit": "Create ticket",
  "intake.submitting": "Sending…",
  "intake.captchaRequired": "Please complete the captcha to continue.",
  "intake.teamRequired": "Please pick a team.",
  "intake.teamPlaceholder": "Pick a team…",
  "intake.protectedBy": "Protected by Cloudflare Turnstile.",
  "intake.thanksHeading": "Thanks!",
  "intake.thanksDefault": "Your ticket #{n} has been created.",
  "intake.thanksReplyHint": "You'll receive a confirmation email. Reply directly to that email to add more information.",
  "intake.unavailableHeading": "Form temporarily unavailable",
  "intake.captchaMissing": "The form is not protected against abuse and cannot be shown. The administrator must configure TURNSTILE_SITE_KEY.",
  "intake.errCaptcha": "Captcha check failed. Please solve the challenge again and resubmit.",
  "intake.errFormNotFound": "This form is no longer available. Please ask your contact for a current link.",
  "intake.errValidation": "Some fields are missing or invalid. Please check and try again.",
  "intake.errRateLimit": "Too many submissions from this address. Please try again later.",
  "intake.errGeneric": "Something went wrong. Please try again later.",
  "language.en": "English",
  "language.nl": "Dutch",
  "language.fr": "French",
  "language.de": "German",

  "role.admin": "Administrator",
  "role.agent": "Support agent",
  "role.viewer": "Viewer",
  "role.none": "No role",

  "common.loading": "Loading…",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.ok": "OK",
  "common.close": "Close",
  "common.delete": "Delete",
  "common.add": "Add",
  "common.confirmDelete": "Confirm delete",
  "common.edit": "Edit",
  "common.search": "Search",
  "common.previous": "Previous",
  "common.next": "Next",
  "common.page": "Page",
  "common.of": "of",
  "common.tryAgain": "Try again",
  "common.refresh": "Refresh",
  "common.notFound": "Page not found.",
  "common.dash": "—",

  "status.new": "New",
  "status.working": "In progress",
  "status.waiting_feedback": "Waiting on customer",
  "status.resolved": "Resolved",
  "status.closed": "Closed",
  "status.canceled": "Cancelled",

  "priority.critical": "Critical",
  "priority.high": "High",
  "priority.medium": "Medium",
  "priority.low": "Low",

  "filter.all": "All",
  "filter.status": "Status",
  "filter.priority": "Priority",
  "filter.sla": "SLA",
  "filter.sla.breached": "Breached",
  "filter.sla.risk": "Approaching",
  "filter.contact": "Contact",
  "filter.allContacts": "Every contact",
  "kanban.move": "Move",
  "kanban.moveTo": "Move to",

  "sla.expired": "SLA breached",
  "sla.minutes": "{n} min",
  "sla.hours": "{n} h",
  "sla.days": "{n} d",
  "time.now": "just now",
  "time.minutesAgo": "{n} min ago",
  "time.hoursAgo": "{n} h ago",
  "time.daysAgo": "{n} d ago",

  "dashboard.title": "Dashboard",
  "dashboard.openTickets": "Open tickets",
  "dashboard.slaAtRisk": "SLA at risk",
  "dashboard.slaBreached": "SLA breached",
  "dashboard.activeTotal": "Active total",
  "dashboard.activeTickets": "Active tickets",
  "dashboard.noOpenTickets": "No open tickets.",

  "ticket.number": "#",
  "ticket.subject": "Subject",
  "ticket.contact": "Contact",
  "ticket.status": "Status",
  "ticket.priority": "Priority",
  "ticket.sla": "SLA",
  "ticket.lastUpdate": "Last update",
  "ticket.assignee": "Assigned to",
  "ticket.team": "Team",
  "ticket.tags": "Tags",
  "ticket.created": "Created",
  "ticket.firstResponse": "First response",
  "ticket.resolved": "Resolved",
  "ticket.closed": "Closed",
  "ticket.reopens": "Reopened",
  "ticket.csat": "CSAT",
  "ticket.searchPlaceholder": "Subject, content…",

  "ticketList.title": "Tickets",
  "ticketList.viewList": "List",
  "ticketList.viewKanban": "Kanban",
  "ticketList.viewTeam": "Team",
  "ticketList.sortHint": "Click to sort, click again to reverse, third click clears.",
  "ticketList.bulkSelected": "{n} selected",
  "ticketList.contactFilter": "Contact:",
  "ticketList.clearContactFilter": "Clear contact filter",
  "ticketList.bulkSelectHint": "Select tickets to assign them to a team.",
  "ticketList.bulkAssign": "Assign team",
  "ticketList.bulkApplying": "Assigning…",
  "ticketList.bulkSetStatus": "Apply status",
  "ticketList.bulkStatusPlaceholder": "Change status to…",
  "ticketList.bulkStatusAria": "New status for selected tickets",
  "ticketList.bulkTeamAria": "New team for selected tickets",
  "ticketList.bulkStatusResult": "Status updated on {n} of {total} ticket(s). {skipped} skipped because the transition was not allowed.",
  "ticketList.bulkStatusResultClean": "Status updated on {n} of {total} ticket(s).",
  "ticketList.selectAll": "Select all",
  "ticketList.selectRow": "Select",
  "ticketList.kanbanEmpty": "Nothing here.",
  "tickets.privateBadge": "Private",
  "ticketList.awaitingReply": "Awaiting reply",
  "ticketDetail.awaitingReply": "Customer is waiting on your reply",
  "ticketDetail.awaitingReplyClear": "Mark as handled — dismiss the awaiting-reply flag",
  "tickets.privateField": "Private ticket",
  "tickets.privateHelp": "Only the assignee can see this ticket.",
  "tickets.inboundAliasField": "Reply from",
  "tickets.inboundAliasDefault": "Auto (default)",
  "tickets.inboundAliasPersonalGroup": "Your addresses",
  "tickets.inboundAliasTeamGroup": "Team addresses",
  "tickets.inboundAliasHelp": "Outbound replies on this ticket will be sent from the chosen address.",
  "tickets.inlineRequired": "Please fill in the required fields.",

  "stats.slaAdherence": "SLA adherence",
  "stats.slaInBreach": "{n} ticket(s) currently in breach",
  "stats.firstResponse": "First response",
  "stats.firstResponseHint": "Average across all tickets",
  "stats.resolutionRate": "Resolution rate",
  "stats.resolvedOf": "{resolved} of {total} resolved or closed",
  "stats.assignedToYou": "Assigned to you",
  "stats.openTotal": "{n} open in total",

  "ticketDetail.aiSuggestReply": "AI reply suggestion",
  "ticketDetail.aiThinking": "AI is thinking…",
  "ticketDetail.aiSummary": "AI summary",
  "ticketDetail.translateToMyLang": "Translate",
  "ticketDetail.showOriginal": "Show original",
  "ticketDetail.retranslate": "Re-translate",
  "ticketDetail.translateReplyToTicket": "Translate reply",
  "ticketDetail.translateReplyHint": "Translate your reply back into the ticket's original language.",
  "ticketDetail.translating": "Translating…",
  "ticketDetail.aiTranslateFailed": "Translation failed: {message}",
  "ticketDetail.aiSummaryHeading": "AI summary",
  "ticketDetail.kbHeading": "Knowledge base",
  "ticketDetail.kbHelp": "Turn this resolved conversation into a knowledge article so the next agent (or AI suggestion) can reuse it.",
  "ticketDetail.kbExtract": "Create knowledge article",
  "ticketDetail.kbExtracting": "Drafting article…",
  "ticketDetail.kbCreated": "Article created. Open it to review and edit:",
  "ticketDetail.kbExtractFailed": "Could not generate a knowledge article: {message}",
  "ticketDetail.internalNote": "Internal note",
  "ticketDetail.replyPlaceholder": "Reply to customer…",
  "ticketDetail.notePlaceholder": "Internal note (only visible to agents)…",
  "ticketDetail.send": "Send reply",
  "ticketDetail.saveNote": "Save note",
  "ticketDetail.sending": "Sending…",
  "ticketDetail.contact": "Contact",
  "ticketDetail.properties": "Properties",
  "ticketDetail.metrics": "Metrics",
  "ticketDetail.noMessages": "No messages yet.",
  "ticketDetail.client": "Customer",
  "ticketDetail.agent": "Agent",
  "ticketDetail.unknown": "Unknown",
  "ticketDetail.noAssignee": "Nobody",
  "ticketDetail.deadline": "Deadline",
  "ticketDetail.noSlaPolicy": "No SLA policy",
  "ticketDetail.aiSuggestionFailed": "AI suggestion failed: {message}",
  "ticketDetail.aiSummaryFailed": "AI summary failed: {message}",
  "ticketDetail.archive": "Archive",
  "ticketDetail.replyShort": "Reply",
  "ticketDetail.prevTicket": "Previous",
  "ticketDetail.nextTicket": "Next",
  "ticketDetail.sentTo": "Sent to",
  "ticketDetail.latestBadge": "Latest",
  "ticketDetail.attachFiles": "Attach files",
  "ticketDetail.attachmentRemove": "Remove attachment",
  "ticketDetail.attachmentDownloadFailed": "Download failed: {message}",
  "ticketDetail.customerHeading": "Customer",
  "ticketDetail.editCustomer": "Edit customer",
  "ticketDetail.expandMessage": "Expand message",
  "ticketDetail.refreshMessage": "Refresh delivery status",
  "ticketDetail.retryMessage": "Retry sending",
  "ticketDetail.retryFailed": "Retry failed: {message}",
  "ticketDetail.statsHeading": "Statistics",

  "contacts.title": "Contacts",
  "contacts.searchPlaceholder": "Name or email…",
  "contacts.clearSelection": "Clear selection",
  "contacts.noMatches": "No matching contacts",
  "contacts.typeToSearch": "Showing the first 200 — type to search all contacts.",
  "contacts.tableName": "Name",
  "contacts.tableEmail": "Email",
  "contacts.tablePhone": "Phone",
  "contacts.tableType": "Type",
  "contacts.tableTickets": "Tickets",
  "contacts.tableCreated": "Created",
  "contacts.ticketsHeading": "Tickets",
  "contacts.noTickets": "No tickets yet.",
  "contacts.viewTicketsHint": "View tickets for this contact",
  "contacts.viewAllTickets": "View all tickets for this contact",

  "knowledge.title": "Knowledge base",
  "knowledge.searchPlaceholder": "Title or content…",
  "knowledge.editArticle": "Edit article",
  "knowledge.newArticle": "New article",
  "knowledge.titleField": "Title",
  "knowledge.contentField": "Content",
  "knowledge.tagsField": "Tags (comma-separated)",
  "knowledge.deleteConfirm": "Delete this article?",
  "knowledge.empty": "No articles found.",
  "knowledge.generate.heading": "Generate articles from product docs",
  "knowledge.generate.intro": "Paste product documentation and let the AI break it down into short Q&A-style articles. Each gets saved as a new entry you can edit afterwards.",
  "knowledge.generate.openButton": "Generate from docs",
  "knowledge.generate.textField": "Product documentation",
  "knowledge.generate.placeholder": "Paste a manual, FAQ, release notes or feature spec here…",
  "knowledge.generate.help": "Aim for at least a paragraph. The AI keeps to what the text says — it won't invent features.",
  "knowledge.generate.generateButton": "Generate articles",
  "knowledge.generate.generating": "Generating…",
  "knowledge.generate.success": "Created {n} new article(s). Refresh-free — they're already in the list below.",
  "knowledge.generate.tooShort": "Provide at least a paragraph of documentation.",

  "settings.title": "Settings",
  "settings.intro": "Email configuration for this organisation. Secrets are stored in the database and never returned to the browser.",
  "settings.outbound.title": "Outbound email",
  "settings.outbound.provider": "Provider",
  "settings.outbound.providerSmtp": "SMTP",
  "settings.outbound.providerResend": "Resend (HTTP API)",
  "settings.outbound.from": "Sender (From)",
  "settings.outbound.fromHelp": "With Resend this address must belong to a verified domain.",
  "settings.outbound.replyTo": "Reply-to (support email)",
  "settings.outbound.smtpHost": "SMTP host",
  "settings.outbound.smtpPort": "SMTP port",
  "settings.outbound.smtpUser": "SMTP user",
  "settings.outbound.smtpPass": "SMTP password",
  "settings.outbound.smtpPassPlaceholder": "Enter a password",
  "settings.outbound.secretSet": "A value is already configured. Leave blank to keep it.",
  "settings.outbound.secretUnset": "No password set yet.",
  "settings.outbound.resendKey": "Resend API key",
  "settings.outbound.resendKeySet": "An API key is already configured. Leave blank to keep it.",
  "settings.outbound.resendKeyUnset": "Generate an API key in the Resend dashboard under API Keys.",
  "settings.inbound.title": "Inbound email",
  "settings.inbound.method": "Method",
  "settings.inbound.methodWebhook": "Webhook (provider POSTs to /api/email/inbound)",
  "settings.inbound.methodImap": "IMAP (server polls a mailbox)",
  "settings.inbound.methodBoth": "Both",
  "settings.inbound.webhookSecret": "Webhook secret",
  "settings.inbound.webhookSecretPlaceholder": "Choose a strong secret",
  "settings.inbound.webhookExplain": "The inbound mail webhook validates the X-Webhook-Secret header against this value.",
  "settings.inbound.webhookUrlOrgHint": "Scoped to organisation {orgId}. The org id in the path links the webhook to this org; the X-Webhook-Secret header still has to match.",
  "settings.outbound.resendWebhookUrlOrgHint": "Scoped to organisation {orgId}. The org id in the path links the webhook to this org; Resend's Svix signature is verified against this org's signing secret.",
  "settings.inbound.webhookSecretSet": "Leave blank to keep the existing secret.",
  "settings.inbound.webhookSecretUnset": "Without a secret the webhook responds 503.",
  "settings.imap.host": "IMAP host",
  "settings.imap.port": "IMAP port",
  "settings.imap.tls": "Use TLS (port 993)",
  "settings.imap.user": "IMAP user",
  "settings.imap.pass": "IMAP password",
  "settings.imap.passSet": "A password is already set. Leave blank to keep it.",
  "settings.imap.passUnset": "Many providers require an app-specific password.",
  "settings.imap.folder": "Folder",
  "settings.imap.pollSeconds": "Poll interval (seconds)",
  "settings.imap.pollSecondsHelp": "Min 15s, max 3600s. Default 120s.",
  "settings.imap.test": "Test IMAP poll",
  "settings.imap.testing": "Polling…",
  "settings.imap.testHint": "Runs a poll right now and reports how many messages were imported. Use this if mails seem to be missing.",
  "settings.imap.testOk": "Polled successfully — {processed} new message(s), last UID {lastUid} ({ms} ms).",
  "settings.imap.testFailed": "Poll failed: {error}",
  "settings.saved": "Settings saved.",
  "settings.noAccess": "You don't have access to settings.",
  "settings.noAccessRoleLabel": "Current role: {roles}.",
  "settings.noAccessNoRole": "Ask an administrator to assign you a role in the DotBots platform.",
  "settings.noAccessUpgrade": "Ask an administrator to upgrade your role to Support agent or Administrator.",
  "settings.readOnly": "Your role ({roles}) only allows reading. Ask an administrator to make changes.",
  "settings.loadFailed": "Settings could not be loaded.",

  "settings.inbound.webhookUrl": "Webhook URL",
  "settings.inbound.payloadHeading": "Expected payload",
  "settings.inbound.payloadHelp": "Configure your inbound mail provider to POST JSON to this URL. Send the secret above as the X-Webhook-Secret header.",
  "settings.outbound.resendWebhookHeading": "Resend delivery webhook",
  "settings.outbound.resendWebhookHelp": "Add this URL to Resend → Webhooks. Resend signs each call with Svix headers (svix-id / svix-timestamp / svix-signature); we verify them and update each message's delivery status.",
  "settings.outbound.resendWebhookUrl": "Webhook URL",
  "settings.outbound.resendWebhookSecret": "Resend signing secret (whsec_…)",
  "settings.outbound.resendWebhookSecretSet": "A signing secret is already configured. Leave blank to keep it.",
  "settings.outbound.resendWebhookSecretUnset": "Copy the signing secret shown when you created the webhook in Resend.",
  "settings.outbound.resendOnly": "(Only used when the outbound provider is Resend.)",

  "settings.tabs.outbound": "Outbound",
  "settings.tabs.inbound": "Inbound",
  "settings.tabs.webhooks": "Webhooks",
  "settings.tabs.templates": "Email templates",
  "settings.templates.heading": "Outbound email templates",
  "settings.templates.intro": "Edit the transactional emails the app sends. Per-language overrides are stored per organisation; built-in defaults ship in English, Dutch, French and German.",
  "settings.templates.defaultLang": "Default outgoing language",
  "settings.templates.defaultLangHelp": "Used when we don't otherwise know which language to send (e.g. tickets created from inbound email).",
  "settings.templates.languages": "Languages",
  "settings.templates.addLanguage": "Add language",
  "settings.templates.addLanguagePrompt": "Language code",
  "settings.templates.addLanguageHelp": "Two- or three-letter IETF code (e.g. it, es, zh-CN). The AI seeds this language with translated copies of the English defaults; you can edit them afterwards.",
  "settings.templates.translateAndAdd": "Translate & add",
  "settings.templates.translating": "Translating…",
  "settings.templates.removeLangAria": "Remove {lang}",
  "settings.templates.removeLangConfirm": "Remove all overrides for {lang}? Built-in languages will fall back to the bundled default; custom languages will disappear from the editor.",
  "settings.templates.langCodeInvalid": "Invalid language code. Use two or three lowercase letters, optionally with a region (e.g. zh-CN).",
  "settings.templates.langExists": "That language is already in the list.",
  "settings.templates.langAdded": "Language added.",
  "settings.templates.langRemoved": "Language removed.",
  "settings.templates.kind.label": "Email",
  "settings.templates.kind.confirmation": "Ticket confirmation",
  "settings.templates.kind.agent_reply": "Agent reply",
  "settings.templates.kind.csat": "Resolved (CSAT)",
  "settings.templates.kind.reopen_ack": "Reopen acknowledgement",
  "settings.templates.subject": "Subject",
  "settings.templates.subjectHelp": "The send layer prepends [#{ticketNumber}] automatically — leave it out here.",
  "settings.templates.body": "Body",
  "settings.templates.placeholdersHelp": "Available placeholders: {ticketNumber}, {subject}, {body} (agent reply only), {csatLink} (CSAT mail only).",
  "settings.templates.resetToDefault": "Reset to default",
  "settings.templates.resetConfirm": "Discard your custom version and fall back to the built-in default?",
  "settings.templates.resetFlash": "Reset to default.",
  "settings.templates.savedFlash": "Template saved.",
  "settings.templates.previewShow": "Show preview",
  "settings.templates.previewHide": "Hide preview",
  "settings.templates.previewHeading": "Preview",
  "settings.templates.previewMeta": "Sample render with ticket #{n} and a fake reply, updated as you type.",
  "settings.templates.previewEmpty": "Body is empty — type something into the editor to see the rendered email here.",
  "settings.templates.defaultUpdated": "Default language updated.",
  "settings.templates.sourceOverride": "Showing your custom version for this language.",
  "settings.templates.sourceBuiltin": "Showing the built-in default. Save to keep your edits as a custom override.",
  "settings.templates.sourceFallback": "No template configured for this language. Falling back to the built-in {lang} default.",
  "settings.summary.outbound": "Outbound",
  "settings.summary.inbound": "Inbound",
  "settings.summary.statusReady": "Ready",
  "settings.summary.statusIncomplete": "Incomplete",
  "settings.summary.statusEmpty": "Not configured",
  "settings.payloadShow": "Show example payload",
  "settings.payloadHide": "Hide example payload",
  "settings.section.smtpCreds": "SMTP credentials",
  "settings.section.imapServer": "IMAP server",
  "settings.section.imapAuth": "IMAP authentication",
  "settings.section.inboundWebhookHeading": "Inbound mail webhook",
  "settings.section.inboundWebhookHelp": "Configure your inbound mail provider to POST mail JSON here.",

  "common.copy": "Copy",
  "common.copied": "Copied",

  "delivery.queued": "Queued",
  "delivery.sent": "Sent",
  "delivery.delivered": "Delivered",
  "delivery.opened": "Opened",
  "delivery.delayed": "Delayed",
  "delivery.bounced": "Bounced",
  "delivery.complained": "Marked as spam",
  "delivery.failed": "Failed",
  "delivery.smtpHint": "SMTP — delivery confirmation not reported by provider.",
  "delivery.errorPrefix": "Error",

  "common.create": "Create",
  "common.creating": "Creating…",
  "common.firstName": "First name",
  "common.lastName": "Last name",
  "common.email": "Email",
  "common.phone": "Phone",
  "common.required": "required",

  "contacts.newButton": "New contact",
  "contacts.backToList": "Back to contacts",
  "contacts.newHeading": "New contact",
  "contacts.customerType": "Type",
  "contacts.customerTypeB2B": "Business (B2B)",
  "contacts.customerTypeB2C": "Consumer (B2C)",
  "contacts.customerTypeVIP": "VIP",
  "contacts.consentEmail": "Allow contact by email",
  "contacts.consentMarketing": "Allow marketing emails",
  "contacts.created": "Contact created.",
  "contacts.updated": "Contact updated.",
  "contacts.editButton": "Edit contact",
  "contacts.editHeading": "Edit contact",

  "tickets.newButton": "New ticket",
  "tickets.tabMine": "My tickets",
  "tickets.tabAll": "All",
  "tickets.tabNoTeam": "No team",
  "tickets.newHeading": "New ticket",
  "tickets.contactField": "Contact",
  "tickets.contactPlaceholder": "Choose a contact…",
  "tickets.contactRequired": "Pick a contact first. Create one via the Contacts page if needed.",
  "tickets.bodyField": "Description",
  "tickets.bodyPlaceholder": "What is the customer asking?",
  "tickets.tagsField": "Tags (comma-separated)",
  "tickets.unassigned": "Unassigned",
  "tickets.noTeam": "No team",
  "tickets.noSla": "No SLA",

  "nav.admin": "Admin",
  "nav.autoArchive": "Auto-archive",
  "autoArchive.title": "Auto-archive rules",
  "autoArchive.intro": "Inbound mail that matches one of these rules is turned into a ticket and immediately closed, so it stays searchable but drops out of the queue.",
  "autoArchive.addTitle": "Add a rule",
  "autoArchive.addButton": "Add rule",
  "autoArchive.listTitle": "Current rules",
  "autoArchive.empty": "No rules yet.",
  "autoArchive.noAccess": "You need an admin role to manage auto-archive rules.",
  "autoArchive.kindLabel": "Match on",
  "autoArchive.patternLabel": "Pattern",
  "autoArchive.noteLabel": "Note (optional)",
  "autoArchive.notePlaceholder": "e.g. Stripe receipts, Instagram story recap",
  "autoArchive.noteFromTicket": "Added from ticket {ticketId}",
  "autoArchive.colKind": "Match on",
  "autoArchive.colPattern": "Pattern",
  "autoArchive.colNote": "Note",
  "autoArchive.deleteConfirmTitle": "Delete rule?",
  "autoArchive.deleteConfirmBody": "Remove the auto-archive rule for '{pattern}'? Future inbound mail that matches will show up in the queue again.",
  "autoArchive.addFromTicketTitle": "Add auto-archive rule from this ticket",
  "autoArchive.addFromTicketShort": "Auto-archive like this",
  "autoArchive.addFromTicketBody": "Any future inbound mail that matches the rule will be ticketed and immediately closed.",
  "autoArchive.alsoCloseThisTicket": "Also close this ticket now",
  "autoArchive.kind.from_domain": "From domain",
  "autoArchive.kind.from_address": "From address (exact)",
  "autoArchive.kind.subject_contains": "Subject contains",
  "autoArchive.help.from_domain": "Matches when the sender's address ends with this domain (e.g. 'mail.instagram.com' catches 'x@mail.instagram.com').",
  "autoArchive.help.from_address": "Matches only this exact address.",
  "autoArchive.help.subject_contains": "Matches when the subject contains this substring, case-insensitive.",
  "autoArchive.applyToInbox": "Also apply to tickets already in the inbox",
  "autoArchive.applyToInboxHelp": "Open tickets whose sender or subject matches the rule are closed too. Already-closed tickets are left alone.",
  "autoArchive.applyRow": "Apply to inbox",
  "autoArchive.applying": "Applying…",
  "autoArchive.sweepResult": "Closed {closed} of {scanned} open tickets.",
  "autoArchive.sweepRowResult": "Closed {closed}/{scanned}",
  "autoArchive.duplicateError": "A rule for this pattern already exists.",
  "admin.title": "Teams & people",
  "admin.intro": "Teams, agents and SLA policies for this organisation. Teams and people coming from the DotBots platform are tagged accordingly.",
  "admin.platformBadge": "Platform",
  "admin.manualBadge": "Manual",
  "admin.sync": "Sync from platform",
  "admin.syncing": "Syncing…",
  "admin.syncIntro": "Pull your account and the teams it belongs to from DotBots Boutique. Other agents are imported automatically the first time they open this app.",
  "admin.syncSuccess": "Synced {count} teams from the platform.",
  "admin.syncSuccessJwt": "Synced from JWT (platform SDK unavailable).",

  "admin.teams.heading": "Teams",
  "admin.teams.newButton": "New team",
  "admin.teams.name": "Name",
  "admin.teams.defaultSla": "Default SLA",
  "admin.teams.source": "Source",
  "admin.teams.deleteConfirm": "Delete this team? Tickets and agents linked to it will be unassigned.",

  "admin.agents.heading": "Agents",
  "admin.agents.newButton": "New agent",
  "admin.agents.team": "Team",
  "admin.agents.platformUserId": "Platform user id",
  "admin.agents.deleteConfirm": "Delete this agent? Their tickets will lose the assignee.",

  "admin.sla.heading": "SLA policies",
  "admin.sla.newButton": "New policy",
  "admin.private.heading": "Private tickets",
  "admin.private.intro": "Open private tickets in your organisation. The body and contact stay hidden — admins can only reassign them to another agent.",
  "admin.private.reassign": "Reassign",
  "admin.private.pickAgent": "Pick an agent…",
  "admin.private.empty": "No open private tickets.",
  "admin.bulk.heading": "Bulk reassign",
  "admin.bulk.intro": "Move every open ticket from one queue to another in one click.",
  "admin.bulk.source": "From",
  "admin.bulk.target": "To",
  "admin.bulk.allPrivate": "All private tickets in the org",
  "admin.bulk.fromAgent": "All tickets from {name}",
  "admin.bulk.onlyPrivate": "Only private tickets",
  "admin.bulk.apply": "Reassign",
  "admin.bulk.applying": "Reassigning…",
  "admin.bulk.success": "{n} ticket(s) reassigned.",
  "admin.forms.heading": "Intake forms",
  "admin.forms.intro": "Generate a public ticket form. Share the URL or paste the iframe snippet on your website.",
  "admin.forms.newButton": "New form",
  "admin.forms.newHeading": "New intake form",
  "admin.forms.editHeading": "Edit intake form",
  "admin.forms.name": "Form name",
  "admin.forms.slug": "URL slug",
  "admin.forms.slugHelp": "Letters, digits and dashes only — used in the public URL.",
  "admin.forms.teamMode": "Team assignment",
  "admin.forms.modeFixed": "Always assign to one team",
  "admin.forms.modeChoose": "Let the visitor pick a team",
  "admin.forms.fixedTeam": "Team",
  "admin.forms.selectableTeams": "Teams the visitor can pick from",
  "admin.forms.selectableTeamsHelp": "Leave all unchecked to allow every team in the org.",
  "admin.forms.titleField": "Title (shown on the form)",
  "admin.forms.titlePlaceholder": "Heading visitors see",
  "admin.forms.titleHelp": "Replaces the form's internal name on the public page. Leave empty to keep showing the name.",
  "admin.forms.introText": "Intro text (optional)",
  "admin.forms.successText": "Confirmation text (optional)",
  "admin.forms.redirectUrl": "Redirect URL (optional)",
  "admin.forms.redirectUrlPlaceholder": "https://www.example.com/thank-you",
  "admin.forms.redirectUrlHelp": "After a successful submission, send visitors to this URL instead of showing the confirmation text. Leave empty to keep the text.",
  "admin.forms.noteHtml": "Small print under the form (HTML allowed)",
  "admin.forms.noteHtmlPlaceholder": "No spam. One confirmation, one reminder the day before.<br>Built with <a href=\"#\">SiteAI on DotBots Boutique</a>.",
  "admin.forms.noteHtmlHelp": "Rendered as a small grey paragraph below the form. Use <br> for line breaks and <a href=\"...\">links</a>.",
  "admin.forms.fixedSubject": "Fixed subject (optional)",
  "admin.forms.fixedSubjectPlaceholder": "e.g. Newsletter signup",
  "admin.forms.fixedSubjectHelp": "Set a subject to use for every ticket from this form. The Subject input on the public form is hidden when this is filled in.",
  "admin.forms.fieldLabelsShow": "Customise field labels & button",
  "admin.forms.fieldLabelsHide": "Hide field labels & button",
  "admin.forms.fieldLabelsHelp": "Override the visitor-facing labels and the submit button caption for this language. Leave empty to keep the AI-translated default.",
  "admin.forms.fieldLabel.name": "Name label",
  "admin.forms.fieldLabel.name.placeholder": "e.g. Your name",
  "admin.forms.fieldLabel.email": "Email label",
  "admin.forms.fieldLabel.email.placeholder": "e.g. Email address",
  "admin.forms.fieldLabel.subject": "Subject label",
  "admin.forms.fieldLabel.subject.placeholder": "e.g. Subject",
  "admin.forms.fieldLabel.description": "Description label",
  "admin.forms.fieldLabel.description.placeholder": "e.g. How can we help?",
  "admin.forms.fieldLabel.team": "Team label",
  "admin.forms.fieldLabel.team.placeholder": "e.g. Department",
  "admin.forms.fieldLabel.teamPlaceholder": "Team placeholder",
  "admin.forms.fieldLabel.teamPlaceholder.placeholder": "e.g. Pick a team…",
  "admin.forms.fieldLabel.submit": "Submit button caption",
  "admin.forms.fieldLabel.submit.placeholder": "e.g. Send my request",
  "admin.forms.publicUrl": "Public URL",
  "admin.forms.embedJs": "Embed code (script — recommended)",
  "admin.forms.embedJsHelp": "Paste this on your website.",
  "admin.forms.embedJsRestyleButton": "View restyling example",
  "admin.forms.embedJsRestyleHeading": "Restyling the embed",
  "admin.forms.embedJsRestyleIntro": "The form uses these class names. Override them from your own CSS to match your site's style.",
  "admin.forms.embedJsRestyleSample": "Sample CSS — paste this into your own stylesheet and adjust to taste:",
  "admin.forms.open": "Open",
  "admin.forms.empty": "No intake forms yet.",
  "admin.forms.deleteConfirm": "Delete this form? Tickets created via the form keep their data; the public URL stops working.",
  "admin.forms.translations": "Translations",
  "admin.forms.addLanguage": "Add language",
  "admin.forms.pickLanguage": "Pick a language…",
  "admin.forms.customLangCode": "or type a code (e.g. zh, pt-BR)",
  "admin.forms.invalidLangCode": "Use an IETF language code: 2-3 letters, optionally a region (e.g. en, zh, pt-BR).",
  "admin.forms.noLanguages": "No languages yet. Add one to start filling in the form copy.",
  "admin.forms.translateAll": "Translate from {from} into the other languages",
  "admin.forms.translateAllHint": "Calls the AI to translate this language's intro and confirmation text into every other added language.",
  "admin.forms.translating": "Translating…",
  "admin.forms.translateEmpty": "Fill in the intro or confirmation text first.",
  "admin.forms.translateNoTargets": "Add at least one other language to translate into.",
  "admin.forms.translateNoSource": "Fill in another language first so we have something to translate from.",
  "admin.forms.fillFrom": "Fill {target} from {from}",
  "admin.forms.fillFromHint": "Translates the {from} content into {target} only — leaves the other languages untouched.",
  "admin.forms.fillFromDisabled": "Fill {target} (no source yet)",
  "admin.sla.firstResponseMins": "First response (min)",
  "admin.sla.resolutionMins": "Resolution (min)",
  "admin.sla.deleteConfirm": "Delete this SLA policy? Tickets and teams using it will keep their data but lose the link.",

  "admin.detailsToggle": "Details",
  "admin.signature": "Email signature",
  "admin.signaturePlaceholder": "— John Doe\nSupport agent",
  "admin.teamSignatureHelp": "Used as a fallback when the agent has no signature of their own.",
  "admin.userSignatureHelp": "Appended to outbound email when this agent replies. Falls back to the team signature when empty.",
  "admin.teamAliases": "Team email addresses",
  "admin.teamAliasesHelp": "Mails arriving at any of these addresses are auto-routed to this team.",
  "admin.userAliases": "Personal email addresses",
  "admin.userAliasesHelp": "Mails arriving at these addresses are auto-assigned to this agent and become invisible to other agents.",
};

const nl: Dict = {
  "appName": "dotSupport",

  "nav.dashboard": "Dashboard",
  "nav.tickets": "Tickets",
  "nav.contacts": "Contacten",
  "nav.knowledge": "Kennisbank",
  "nav.reports": "AI-rapporten",
  "reports.title": "AI-rapporten",
  "reports.intro": "Beschrijf in gewone taal welke tickets je wil exporteren en wat er in het bestand moet komen. AI zet je vraag om in een filter, toont hoeveel tickets matchen, en genereert pas dan het bestand.",
  "reports.queryLabel": "Wat wil je exporteren?",
  "reports.queryHelp": "Voorbeeld: 'Vat de reacties samen op mijn campagne-mail met XYZ in het Nederlandse onderwerp en ZYX in het Franse. Zet email, het volledige antwoord en een AI-samenvatting per antwoord in het bestand.'",
  "reports.queryPlaceholder": "Beschrijf welke tickets je wil exporteren…",
  "reports.formatLabel": "Bestandstype",
  "reports.analyseButton": "Analyseer",
  "reports.planning": "Analyseren…",
  "reports.previewTitle": "Voorbeeld",
  "reports.explanationLabel": "Interpretatie",
  "reports.matchCount": "Matchende tickets",
  "reports.cappedAt": "gelimiteerd op",
  "reports.columns": "Kolommen",
  "reports.colField": "veld",
  "reports.colAi": "AI",
  "reports.estimatedCost": "Geschatte AI-kost",
  "reports.aiCalls": "AI-oproepen",
  "reports.sampleTitle": "Steekproef (eerste 3)",
  "reports.colSubject": "Onderwerp",
  "reports.colEmail": "Email",
  "reports.colStatus": "Status",
  "reports.generateAndDownload": "Genereer & download",
  "reports.generating": "Bezig met genereren…",
  "reports.largeJobWarning": "Dit kan even duren — de samenvattingen worden gebundeld, maar het model moet elk ticket wel verwerken.",
  "reports.progress": "{done} van {total} tickets verwerkt…",
  "reports.cancelled": "Het rapport werd geannuleerd.",
  "reports.historyTitle": "Opgeslagen rapporten",
  "reports.historyEmpty": "Nog geen rapporten opgeslagen — het eerste rapport dat je genereert komt hier te staan.",
  "reports.histWhen": "Wanneer",
  "reports.histFormat": "Formaat",
  "reports.histRows": "Rijen",
  "reports.histQuery": "Vraag",
  "reports.histStatus": "Status",
  "reports.download": "Downloaden",
  "reports.deleteConfirmTitle": "Rapport verwijderen?",
  "reports.deleteConfirmBody": "Definitief '{name}' verwijderen? Het bestand kan niet worden hersteld.",
  "reports.jobStartFailed": "Kon de rapport-taak niet starten — de backend is misschien nog aan het deployen. Probeer over een minuutje opnieuw.",
  "reports.jobStatusUnreadable": "De server antwoordt niet meer op de status-check. Ververs de pagina en probeer opnieuw.",
  "reports.noAccessTitle": "Geen toegang",
  "reports.noAccessBody": "Je rol heeft geen AI-toegang. Vraag een beheerder om ze toe te kennen als je AI-rapporten wil draaien.",
  "nav.settings": "Instellingen",

  "language.label": "Taal",
  "intake.name": "Naam",
  "intake.email": "E-mailadres",
  "intake.subject": "Onderwerp",
  "intake.description": "Beschrijving",
  "intake.submit": "Ticket aanmaken",
  "intake.submitting": "Verzenden…",
  "intake.captchaRequired": "Bevestig de captcha om door te gaan.",
  "intake.teamRequired": "Kies een team.",
  "intake.teamPlaceholder": "Kies een team…",
  "intake.protectedBy": "Beschermd door Cloudflare Turnstile.",
  "intake.thanksHeading": "Bedankt!",
  "intake.thanksDefault": "Je ticket #{n} is aangemaakt.",
  "intake.thanksReplyHint": "Je ontvangt een bevestigingsmail. Antwoord rechtstreeks op die mail om extra info mee te geven.",
  "intake.unavailableHeading": "Formulier tijdelijk niet beschikbaar",
  "intake.captchaMissing": "Het formulier is niet beveiligd tegen misbruik en kan daarom niet getoond worden. De beheerder moet TURNSTILE_SITE_KEY configureren.",
  "intake.errCaptcha": "De captcha-controle is mislukt. Los de uitdaging opnieuw op en verzend opnieuw.",
  "intake.errFormNotFound": "Dit formulier is niet meer beschikbaar. Vraag je contact om een actuele link.",
  "intake.errValidation": "Sommige velden zijn niet (correct) ingevuld. Controleer en probeer opnieuw.",
  "intake.errRateLimit": "Te veel inzendingen vanaf dit adres. Probeer het later opnieuw.",
  "intake.errGeneric": "Er is iets misgegaan. Probeer het later opnieuw.",
  "language.en": "Engels",
  "language.nl": "Nederlands",
  "language.fr": "Frans",
  "language.de": "Duits",

  "role.admin": "Administrator",
  "role.agent": "Support-agent",
  "role.viewer": "Viewer",
  "role.none": "Geen rol",

  "common.loading": "Laden…",
  "common.save": "Opslaan",
  "common.saving": "Opslaan…",
  "common.cancel": "Annuleren",
  "common.confirm": "Bevestigen",
  "common.ok": "OK",
  "common.close": "Sluiten",
  "common.add": "Toevoegen",
  "common.delete": "Verwijderen",
  "common.confirmDelete": "Bevestig verwijderen",
  "common.edit": "Bewerken",
  "common.search": "Zoeken",
  "common.previous": "Vorige",
  "common.next": "Volgende",
  "common.page": "Pagina",
  "common.of": "/",
  "common.tryAgain": "Opnieuw proberen",
  "common.refresh": "Vernieuwen",
  "common.notFound": "Pagina niet gevonden.",
  "common.dash": "—",

  "status.new": "Nieuw",
  "status.working": "In behandeling",
  "status.waiting_feedback": "Wacht op klant",
  "status.resolved": "Opgelost",
  "status.closed": "Gesloten",
  "status.canceled": "Geannuleerd",

  "priority.critical": "Kritiek",
  "priority.high": "Hoog",
  "priority.medium": "Normaal",
  "priority.low": "Laag",

  "filter.all": "Alle",
  "filter.status": "Status",
  "filter.priority": "Prioriteit",
  "filter.sla": "SLA",
  "filter.sla.breached": "Overschreden",
  "filter.sla.risk": "Bijna",
  "filter.contact": "Contact",
  "filter.allContacts": "Elk contact",
  "kanban.move": "Verplaats",
  "kanban.moveTo": "Verplaats naar",

  "sla.expired": "SLA verstreken",
  "sla.minutes": "{n} min",
  "sla.hours": "{n} u",
  "sla.days": "{n} d",
  "time.now": "zonet",
  "time.minutesAgo": "{n} min geleden",
  "time.hoursAgo": "{n} u geleden",
  "time.daysAgo": "{n} d geleden",

  "dashboard.title": "Dashboard",
  "dashboard.openTickets": "Open tickets",
  "dashboard.slaAtRisk": "SLA in gevaar",
  "dashboard.slaBreached": "SLA verstreken",
  "dashboard.activeTotal": "Totaal actief",
  "dashboard.activeTickets": "Actieve tickets",
  "dashboard.noOpenTickets": "Geen openstaande tickets.",

  "ticket.number": "#",
  "ticket.subject": "Onderwerp",
  "ticket.contact": "Contact",
  "ticket.status": "Status",
  "ticket.priority": "Prioriteit",
  "ticket.sla": "SLA",
  "ticket.lastUpdate": "Laatste update",
  "ticket.assignee": "Toegewezen aan",
  "ticket.team": "Team",
  "ticket.tags": "Tags",
  "ticket.created": "Aangemaakt",
  "ticket.firstResponse": "1e reactie",
  "ticket.resolved": "Opgelost",
  "ticket.closed": "Gesloten",
  "ticket.reopens": "Heropend",
  "ticket.csat": "CSAT",
  "ticket.searchPlaceholder": "Onderwerp, inhoud…",

  "ticketList.title": "Tickets",
  "ticketList.viewList": "Lijst",
  "ticketList.viewKanban": "Kanban",
  "ticketList.viewTeam": "Team",
  "ticketList.sortHint": "Klik om te sorteren, klik opnieuw om te omkeren, derde klik wist.",
  "ticketList.bulkSelected": "{n} geselecteerd",
  "ticketList.contactFilter": "Contact:",
  "ticketList.clearContactFilter": "Contactfilter wissen",
  "ticketList.bulkSelectHint": "Selecteer tickets om ze aan een team toe te wijzen.",
  "ticketList.bulkAssign": "Team toewijzen",
  "ticketList.bulkApplying": "Bezig…",
  "ticketList.bulkSetStatus": "Status toepassen",
  "ticketList.bulkStatusPlaceholder": "Status wijzigen naar…",
  "ticketList.bulkStatusAria": "Nieuwe status voor geselecteerde tickets",
  "ticketList.bulkTeamAria": "Nieuw team voor geselecteerde tickets",
  "ticketList.bulkStatusResult": "Status bijgewerkt op {n} van {total} ticket(s). {skipped} overgeslagen omdat de overgang niet toegelaten was.",
  "ticketList.bulkStatusResultClean": "Status bijgewerkt op {n} van {total} ticket(s).",
  "ticketList.selectAll": "Alles selecteren",
  "ticketList.selectRow": "Selecteren",
  "ticketList.kanbanEmpty": "Niets hier.",
  "tickets.privateBadge": "Privé",
  "ticketList.awaitingReply": "Wacht op antwoord",
  "ticketDetail.awaitingReply": "Klant wacht op jouw antwoord",
  "ticketDetail.awaitingReplyClear": "Markeer als afgehandeld — wacht-op-antwoord vlag wissen",
  "tickets.privateField": "Privé ticket",
  "tickets.privateHelp": "Enkel de toegewezen agent kan dit ticket zien.",
  "tickets.inboundAliasField": "Verzenden vanaf",
  "tickets.inboundAliasDefault": "Automatisch (standaard)",
  "tickets.inboundAliasPersonalGroup": "Jouw adressen",
  "tickets.inboundAliasTeamGroup": "Team-adressen",
  "tickets.inboundAliasHelp": "Uitgaande antwoorden op dit ticket vertrekken vanaf het gekozen adres.",
  "tickets.inlineRequired": "Vul de verplichte velden in.",

  "stats.slaAdherence": "SLA-naleving",
  "stats.slaInBreach": "{n} ticket(s) momenteel in breach",
  "stats.firstResponse": "Eerste reactie",
  "stats.firstResponseHint": "Gemiddelde over alle tickets",
  "stats.resolutionRate": "Oplosgraad",
  "stats.resolvedOf": "{resolved} van {total} opgelost of gesloten",
  "stats.assignedToYou": "Aan jou toegewezen",
  "stats.openTotal": "{n} open in totaal",

  "ticketDetail.aiSuggestReply": "AI antwoordsuggestie",
  "ticketDetail.translateToMyLang": "Vertalen",
  "ticketDetail.showOriginal": "Toon origineel",
  "ticketDetail.retranslate": "Opnieuw vertalen",
  "ticketDetail.translateReplyToTicket": "Antwoord vertalen",
  "ticketDetail.translateReplyHint": "Vertaal je antwoord terug naar de oorspronkelijke taal van het ticket.",
  "ticketDetail.translating": "Bezig met vertalen…",
  "ticketDetail.aiTranslateFailed": "Vertalen mislukt: {message}",
  "ticketDetail.aiThinking": "AI aan het denken…",
  "ticketDetail.aiSummary": "AI samenvatting",
  "ticketDetail.aiSummaryHeading": "AI-samenvatting",
  "ticketDetail.kbHeading": "Kennisbank",
  "ticketDetail.kbHelp": "Maak van dit opgeloste ticket een kennisbankartikel zodat de volgende agent (of AI-suggestie) er meteen iets aan heeft.",
  "ticketDetail.kbExtract": "Kennisartikel maken",
  "ticketDetail.kbExtracting": "Artikel opstellen…",
  "ticketDetail.kbCreated": "Artikel aangemaakt. Klik om na te kijken en aan te passen:",
  "ticketDetail.kbExtractFailed": "Kennisartikel maken mislukte: {message}",
  "ticketDetail.internalNote": "Interne notitie",
  "ticketDetail.replyPlaceholder": "Antwoord aan klant…",
  "ticketDetail.notePlaceholder": "Interne notitie (enkel zichtbaar voor agents)…",
  "ticketDetail.send": "Verstuur antwoord",
  "ticketDetail.saveNote": "Notitie opslaan",
  "ticketDetail.sending": "Verzenden…",
  "ticketDetail.contact": "Contact",
  "ticketDetail.properties": "Eigenschappen",
  "ticketDetail.metrics": "Metrics",
  "ticketDetail.noMessages": "Nog geen berichten.",
  "ticketDetail.client": "Klant",
  "ticketDetail.agent": "Agent",
  "ticketDetail.unknown": "Onbekend",
  "ticketDetail.noAssignee": "Niemand",
  "ticketDetail.deadline": "Deadline",
  "ticketDetail.noSlaPolicy": "Geen SLA-beleid",
  "ticketDetail.aiSuggestionFailed": "AI suggestie mislukt: {message}",
  "ticketDetail.aiSummaryFailed": "AI samenvatting mislukt: {message}",
  "ticketDetail.archive": "Archiveren",
  "ticketDetail.replyShort": "Beantwoorden",
  "ticketDetail.prevTicket": "Vorige",
  "ticketDetail.nextTicket": "Volgende",
  "ticketDetail.sentTo": "Verzonden naar",
  "ticketDetail.latestBadge": "Laatste",
  "ticketDetail.attachFiles": "Bijlages toevoegen",
  "ticketDetail.attachmentRemove": "Bijlage verwijderen",
  "ticketDetail.attachmentDownloadFailed": "Download mislukt: {message}",
  "ticketDetail.customerHeading": "Klantgegevens",
  "ticketDetail.editCustomer": "Klant bewerken",
  "ticketDetail.expandMessage": "Bericht vergroten",
  "ticketDetail.refreshMessage": "Aflever-status verversen",
  "ticketDetail.retryMessage": "Opnieuw verzenden",
  "ticketDetail.retryFailed": "Opnieuw verzenden mislukt: {message}",
  "ticketDetail.statsHeading": "Statistieken",

  "contacts.title": "Contacten",
  "contacts.searchPlaceholder": "Naam of e-mail…",
  "contacts.clearSelection": "Selectie wissen",
  "contacts.noMatches": "Geen contactpersonen gevonden",
  "contacts.typeToSearch": "De eerste 200 worden getoond — typ om alle contacten te doorzoeken.",
  "contacts.tableName": "Naam",
  "contacts.tableEmail": "E-mail",
  "contacts.tablePhone": "Telefoon",
  "contacts.tableType": "Type",
  "contacts.tableTickets": "Tickets",
  "contacts.tableCreated": "Aangemaakt",
  "contacts.ticketsHeading": "Tickets",
  "contacts.noTickets": "Nog geen tickets.",
  "contacts.viewTicketsHint": "Tickets van dit contact tonen",
  "contacts.viewAllTickets": "Alle tickets voor dit contact tonen",

  "knowledge.title": "Kennisbank",
  "knowledge.searchPlaceholder": "Titel of inhoud…",
  "knowledge.editArticle": "Artikel bewerken",
  "knowledge.newArticle": "Nieuw artikel",
  "knowledge.titleField": "Titel",
  "knowledge.contentField": "Inhoud",
  "knowledge.tagsField": "Tags (komma-gescheiden)",
  "knowledge.deleteConfirm": "Artikel verwijderen?",
  "knowledge.empty": "Geen artikelen gevonden.",
  "knowledge.generate.heading": "Artikels genereren uit productdocumentatie",
  "knowledge.generate.intro": "Plak documentatie en laat de AI er korte Q&A-artikels van maken. Elk artikel wordt bewaard en kan je nadien aanpassen.",
  "knowledge.generate.openButton": "Genereren uit docs",
  "knowledge.generate.textField": "Productdocumentatie",
  "knowledge.generate.placeholder": "Plak hier een handleiding, FAQ, release notes of feature-omschrijving…",
  "knowledge.generate.help": "Mik op minstens één paragraaf. De AI blijft bij wat in de tekst staat — er worden geen features verzonnen.",
  "knowledge.generate.generateButton": "Artikels genereren",
  "knowledge.generate.generating": "Bezig met genereren…",
  "knowledge.generate.success": "{n} nieuwe artikel(s) aangemaakt. Ze staan al in de lijst hieronder.",
  "knowledge.generate.tooShort": "Voeg minstens één paragraaf documentatie toe.",

  "settings.title": "Instellingen",
  "settings.intro": "E-mailconfiguratie voor deze organisatie. Geheimen worden in de database opgeslagen en nooit teruggestuurd naar de browser.",
  "settings.outbound.title": "Uitgaande e-mail",
  "settings.outbound.provider": "Provider",
  "settings.outbound.providerSmtp": "SMTP",
  "settings.outbound.providerResend": "Resend (HTTP API)",
  "settings.outbound.from": "Afzender (From)",
  "settings.outbound.fromHelp": "Bij Resend moet dit adres aan een geverifieerd domein gekoppeld zijn.",
  "settings.outbound.replyTo": "Reply-to (support-e-mail)",
  "settings.outbound.smtpHost": "SMTP-host",
  "settings.outbound.smtpPort": "SMTP-poort",
  "settings.outbound.smtpUser": "SMTP-gebruiker",
  "settings.outbound.smtpPass": "SMTP-wachtwoord",
  "settings.outbound.smtpPassPlaceholder": "Voer een wachtwoord in",
  "settings.outbound.secretSet": "Een waarde is al ingesteld. Laat leeg om die ongewijzigd te laten.",
  "settings.outbound.secretUnset": "Nog geen wachtwoord ingesteld.",
  "settings.outbound.resendKey": "Resend API-key",
  "settings.outbound.resendKeySet": "Een API-key is al ingesteld. Laat leeg om die ongewijzigd te laten.",
  "settings.outbound.resendKeyUnset": "Genereer een API-key in het Resend-dashboard onder API Keys.",
  "settings.inbound.title": "Inkomende e-mail",
  "settings.inbound.method": "Methode",
  "settings.inbound.methodWebhook": "Webhook (provider POST naar /api/email/inbound)",
  "settings.inbound.methodImap": "IMAP (server pollt mailbox)",
  "settings.inbound.methodBoth": "Beide",
  "settings.inbound.webhookSecret": "Webhook secret",
  "settings.inbound.webhookSecretPlaceholder": "Kies een sterk geheim",
  "settings.inbound.webhookExplain": "De inkomende-mail-webhook valideert X-Webhook-Secret tegen deze waarde.",
  "settings.inbound.webhookUrlOrgHint": "Gekoppeld aan organisatie {orgId}. De org-id in het pad linkt de webhook expliciet aan deze organisatie; X-Webhook-Secret moet nog steeds overeenkomen.",
  "settings.outbound.resendWebhookUrlOrgHint": "Gekoppeld aan organisatie {orgId}. De org-id in het pad linkt de webhook expliciet aan deze organisatie; de Svix-signatuur van Resend wordt geverifieerd tegen deze org's signing secret.",
  "settings.inbound.webhookSecretSet": "Laat leeg om het bestaande geheim te behouden.",
  "settings.inbound.webhookSecretUnset": "Zonder geheim antwoordt de webhook met 503.",
  "settings.imap.host": "IMAP-host",
  "settings.imap.port": "IMAP-poort",
  "settings.imap.tls": "TLS gebruiken (poort 993)",
  "settings.imap.user": "IMAP-gebruiker",
  "settings.imap.pass": "IMAP-wachtwoord",
  "settings.imap.passSet": "Een wachtwoord is al ingesteld. Laat leeg om het ongewijzigd te laten.",
  "settings.imap.passUnset": "Veel providers vragen een app-specifiek wachtwoord.",
  "settings.imap.folder": "Map",
  "settings.imap.pollSeconds": "Poll-interval (seconden)",
  "settings.imap.pollSecondsHelp": "Minimaal 15s, maximaal 3600s. Standaard 120s.",
  "settings.imap.test": "IMAP-poll testen",
  "settings.imap.testing": "Pollen…",
  "settings.imap.testHint": "Voert nu een poll uit en toont hoeveel berichten ingelezen werden. Handig wanneer mails uitblijven.",
  "settings.imap.testOk": "Poll geslaagd — {processed} nieuw bericht(en), laatste UID {lastUid} ({ms} ms).",
  "settings.imap.testFailed": "Poll mislukt: {error}",
  "settings.saved": "Instellingen opgeslagen.",
  "settings.noAccess": "Je hebt geen toegang tot de instellingen.",
  "settings.noAccessRoleLabel": "Huidige rol: {roles}.",
  "settings.noAccessNoRole": "Vraag een administrator om in het DotBots-platform een rol aan jouw account toe te kennen.",
  "settings.noAccessUpgrade": "Vraag een administrator om je rol uit te breiden naar Support-agent of Administrator.",
  "settings.readOnly": "Je rol ({roles}) staat enkel lezen toe. Vraag een administrator om wijzigingen door te voeren.",
  "settings.loadFailed": "Instellingen konden niet worden geladen.",

  "settings.inbound.webhookUrl": "Webhook-URL",
  "settings.inbound.payloadHeading": "Verwachte payload",
  "settings.inbound.payloadHelp": "Configureer je inbound-mailprovider om JSON te POST'en naar deze URL. Stuur het bovenstaande geheim mee als X-Webhook-Secret-header.",
  "settings.outbound.resendWebhookHeading": "Resend delivery webhook",
  "settings.outbound.resendWebhookHelp": "Voeg deze URL toe in Resend → Webhooks. Resend signeert elke call met Svix-headers (svix-id / svix-timestamp / svix-signature); wij verifiëren ze en updaten per bericht de delivery status.",
  "settings.outbound.resendWebhookUrl": "Webhook-URL",
  "settings.outbound.resendWebhookSecret": "Resend signing secret (whsec_…)",
  "settings.outbound.resendWebhookSecretSet": "Een signing-secret is al ingesteld. Laat leeg om die ongewijzigd te laten.",
  "settings.outbound.resendWebhookSecretUnset": "Kopieer het signing-secret dat Resend toonde bij het aanmaken van de webhook.",
  "settings.outbound.resendOnly": "(Enkel gebruikt wanneer Resend de uitgaande provider is.)",

  "settings.tabs.outbound": "Uitgaand",
  "settings.tabs.inbound": "Inkomend",
  "settings.tabs.webhooks": "Webhooks",
  "settings.tabs.templates": "Mailteksten",
  "settings.templates.heading": "Uitgaande mailteksten",
  "settings.templates.intro": "Pas de transactionele mails aan die de app verstuurt. Standaardteksten zijn voorzien in Engels, Nederlands, Frans en Duits; je kan extra talen toevoegen.",
  "settings.templates.defaultLang": "Standaardtaal voor uitgaande mails",
  "settings.templates.defaultLangHelp": "Wordt gebruikt wanneer we de taal niet anders kennen (bv. tickets aangemaakt via inkomende mail).",
  "settings.templates.languages": "Talen",
  "settings.templates.addLanguage": "Taal toevoegen",
  "settings.templates.addLanguagePrompt": "Taalcode",
  "settings.templates.addLanguageHelp": "Twee- of drieletterige IETF-code (bv. it, es, zh-CN). De AI zaait die taal met vertaalde versies van de Engelse standaarden; nadien kan je ze nog aanpassen.",
  "settings.templates.translateAndAdd": "Vertalen & toevoegen",
  "settings.templates.translating": "Vertalen…",
  "settings.templates.removeLangAria": "{lang} verwijderen",
  "settings.templates.removeLangConfirm": "Alle overrides voor {lang} verwijderen? Ingebouwde talen vallen terug op de standaard; aangepaste talen verdwijnen uit de editor.",
  "settings.templates.langCodeInvalid": "Ongeldige taalcode. Gebruik twee of drie kleine letters, optioneel met een regio (bv. zh-CN).",
  "settings.templates.langExists": "Deze taal staat al in de lijst.",
  "settings.templates.langAdded": "Taal toegevoegd.",
  "settings.templates.langRemoved": "Taal verwijderd.",
  "settings.templates.kind.label": "E-mail",
  "settings.templates.kind.confirmation": "Ticketbevestiging",
  "settings.templates.kind.agent_reply": "Antwoord van agent",
  "settings.templates.kind.csat": "Opgelost (CSAT)",
  "settings.templates.kind.reopen_ack": "Heropening bevestigd",
  "settings.templates.subject": "Onderwerp",
  "settings.templates.subjectHelp": "De verzendlaag plaatst automatisch [#{ticketNumber}] vooraan — laat dat hier weg.",
  "settings.templates.body": "Tekst",
  "settings.templates.placeholdersHelp": "Beschikbare plaatsvervangers: {ticketNumber}, {subject}, {body} (alleen agent reply), {csatLink} (alleen CSAT-mail).",
  "settings.templates.resetToDefault": "Herstel naar standaard",
  "settings.templates.resetConfirm": "Je aangepaste versie weggooien en terugvallen op de ingebouwde standaard?",
  "settings.templates.resetFlash": "Hersteld naar standaard.",
  "settings.templates.savedFlash": "Sjabloon opgeslagen.",
  "settings.templates.previewShow": "Voorbeeld tonen",
  "settings.templates.previewHide": "Voorbeeld verbergen",
  "settings.templates.previewHeading": "Voorbeeld",
  "settings.templates.previewMeta": "Rendering met ticket #{n} en een nep-antwoord, live bijgewerkt terwijl je typt.",
  "settings.templates.previewEmpty": "Tekst is leeg — typ iets in de editor om de gerenderde mail hier te zien.",
  "settings.templates.defaultUpdated": "Standaardtaal bijgewerkt.",
  "settings.templates.sourceOverride": "Aangepaste versie voor deze taal.",
  "settings.templates.sourceBuiltin": "Ingebouwde standaard. Klik Bewaren om een aangepaste override op te slaan.",
  "settings.templates.sourceFallback": "Geen sjabloon geconfigureerd voor deze taal. Valt terug op de ingebouwde {lang}-standaard.",
  "settings.summary.outbound": "Uitgaand",
  "settings.summary.inbound": "Inkomend",
  "settings.summary.statusReady": "Klaar",
  "settings.summary.statusIncomplete": "Onvolledig",
  "settings.summary.statusEmpty": "Niet geconfigureerd",
  "settings.payloadShow": "Voorbeeldpayload tonen",
  "settings.payloadHide": "Voorbeeldpayload verbergen",
  "settings.section.smtpCreds": "SMTP-aanmeldgegevens",
  "settings.section.imapServer": "IMAP-server",
  "settings.section.imapAuth": "IMAP-aanmelding",
  "settings.section.inboundWebhookHeading": "Inkomende-mail-webhook",
  "settings.section.inboundWebhookHelp": "Configureer je inbound-mailprovider om mail-JSON hierheen te POST'en.",

  "common.copy": "Kopiëren",
  "common.copied": "Gekopieerd",

  "delivery.queued": "Wachtrij",
  "delivery.sent": "Verzonden",
  "delivery.delivered": "Bezorgd",
  "delivery.opened": "Geopend",
  "delivery.delayed": "Vertraagd",
  "delivery.bounced": "Bounce",
  "delivery.complained": "Spam-melding",
  "delivery.failed": "Mislukt",
  "delivery.smtpHint": "SMTP — provider rapporteert geen bezorgbevestiging.",
  "delivery.errorPrefix": "Fout",

  "common.create": "Aanmaken",
  "common.creating": "Aanmaken…",
  "common.firstName": "Voornaam",
  "common.lastName": "Achternaam",
  "common.email": "E-mail",
  "common.phone": "Telefoon",
  "common.required": "verplicht",

  "contacts.newButton": "Nieuw contact",
  "contacts.backToList": "Terug naar contacten",
  "contacts.newHeading": "Nieuw contact",
  "contacts.customerType": "Type",
  "contacts.customerTypeB2B": "Zakelijk (B2B)",
  "contacts.customerTypeB2C": "Particulier (B2C)",
  "contacts.customerTypeVIP": "VIP",
  "contacts.consentEmail": "Mag gecontacteerd worden via e-mail",
  "contacts.consentMarketing": "Mag marketingmails ontvangen",
  "contacts.created": "Contact aangemaakt.",
  "contacts.updated": "Contact bijgewerkt.",
  "contacts.editButton": "Contact bewerken",
  "contacts.editHeading": "Contact bewerken",

  "tickets.newButton": "Nieuw ticket",
  "tickets.tabMine": "Mijn tickets",
  "tickets.tabAll": "Alle",
  "tickets.tabNoTeam": "Zonder team",
  "tickets.newHeading": "Nieuw ticket",
  "tickets.contactField": "Contact",
  "tickets.contactPlaceholder": "Kies een contact…",
  "tickets.contactRequired": "Kies eerst een contact. Maak er een aan via Contacten als het nog niet bestaat.",
  "tickets.bodyField": "Beschrijving",
  "tickets.bodyPlaceholder": "Wat vraagt de klant?",
  "tickets.tagsField": "Tags (komma-gescheiden)",
  "tickets.unassigned": "Niet toegewezen",
  "tickets.noTeam": "Geen team",
  "tickets.noSla": "Geen SLA",

  "nav.admin": "Beheer",
  "nav.autoArchive": "Auto-archiveren",
  "autoArchive.title": "Auto-archiveer regels",
  "autoArchive.intro": "Inkomende mail die op één van deze regels matcht wordt een ticket en meteen gesloten — zo blijft hij vindbaar maar valt hij uit je werklijst.",
  "autoArchive.addTitle": "Regel toevoegen",
  "autoArchive.addButton": "Regel toevoegen",
  "autoArchive.listTitle": "Bestaande regels",
  "autoArchive.empty": "Nog geen regels.",
  "autoArchive.noAccess": "Je hebt admin-rechten nodig om auto-archive regels te beheren.",
  "autoArchive.kindLabel": "Match op",
  "autoArchive.patternLabel": "Patroon",
  "autoArchive.noteLabel": "Notitie (optioneel)",
  "autoArchive.notePlaceholder": "bv. Stripe-ontvangsten, Instagram story recap",
  "autoArchive.noteFromTicket": "Toegevoegd vanaf ticket {ticketId}",
  "autoArchive.colKind": "Match op",
  "autoArchive.colPattern": "Patroon",
  "autoArchive.colNote": "Notitie",
  "autoArchive.deleteConfirmTitle": "Regel verwijderen?",
  "autoArchive.deleteConfirmBody": "De auto-archive regel voor '{pattern}' verwijderen? Nieuwe inkomende mail die matcht komt weer in de queue.",
  "autoArchive.addFromTicketTitle": "Auto-archive regel maken vanuit dit ticket",
  "autoArchive.addFromTicketShort": "Zo'n mail auto-archiveren",
  "autoArchive.addFromTicketBody": "Elke toekomstige inkomende mail die matcht wordt meteen als gesloten ticket aangemaakt.",
  "autoArchive.alsoCloseThisTicket": "Sluit dit ticket ook meteen",
  "autoArchive.kind.from_domain": "Van-domein",
  "autoArchive.kind.from_address": "Van-adres (exact)",
  "autoArchive.kind.subject_contains": "Onderwerp bevat",
  "autoArchive.help.from_domain": "Matcht wanneer het van-adres eindigt op dit domein (bv. 'mail.instagram.com' vangt 'x@mail.instagram.com').",
  "autoArchive.help.from_address": "Matcht alleen dit exacte adres.",
  "autoArchive.help.subject_contains": "Matcht wanneer het onderwerp dit stukje tekst bevat, hoofdletterongevoelig.",
  "autoArchive.applyToInbox": "Ook toepassen op tickets die al in de inbox staan",
  "autoArchive.applyToInboxHelp": "Open tickets waarvan de afzender of het onderwerp matcht worden ook gesloten. Al gesloten tickets blijven ongemoeid.",
  "autoArchive.applyRow": "Toepassen op inbox",
  "autoArchive.applying": "Bezig…",
  "autoArchive.sweepResult": "{closed} van de {scanned} open tickets gesloten.",
  "autoArchive.sweepRowResult": "{closed}/{scanned} gesloten",
  "autoArchive.duplicateError": "Er bestaat al een regel met dit patroon.",
  "admin.title": "Teams & medewerkers",
  "admin.intro": "Teams, agents en SLA-beleid voor deze organisatie. Teams en mensen die uit het DotBots-platform komen zijn als zodanig gemarkeerd.",
  "admin.platformBadge": "Platform",
  "admin.manualBadge": "Handmatig",
  "admin.sync": "Synchroniseren met platform",
  "admin.syncing": "Synchroniseren…",
  "admin.syncIntro": "Haal je account en de teams waartoe je behoort op uit DotBots Boutique. Andere agents worden automatisch toegevoegd zodra ze de app openen.",
  "admin.syncSuccess": "{count} team(s) opgehaald uit het platform.",
  "admin.syncSuccessJwt": "Gesynchroniseerd via JWT (platform-SDK niet beschikbaar).",

  "admin.teams.heading": "Teams",
  "admin.teams.newButton": "Nieuw team",
  "admin.teams.name": "Naam",
  "admin.teams.defaultSla": "Standaard-SLA",
  "admin.teams.source": "Bron",
  "admin.teams.deleteConfirm": "Dit team verwijderen? Tickets en agents met dit team verliezen hun teamlink.",

  "admin.agents.heading": "Agents",
  "admin.agents.newButton": "Nieuwe agent",
  "admin.agents.team": "Team",
  "admin.agents.platformUserId": "Platform gebruikers-id",
  "admin.agents.deleteConfirm": "Deze agent verwijderen? Zijn/haar tickets verliezen de toewijzing.",

  "admin.sla.heading": "SLA-beleid",
  "admin.private.heading": "Privé tickets",
  "admin.private.intro": "Open privé tickets in jouw organisatie. De inhoud en het contact blijven verborgen — admins kunnen ze enkel toewijzen aan een andere agent.",
  "admin.private.reassign": "Toewijzen",
  "admin.private.pickAgent": "Kies een agent…",
  "admin.private.empty": "Geen open privé tickets.",
  "admin.bulk.heading": "Bulk toewijzen",
  "admin.bulk.intro": "Verplaats elk open ticket van de ene wachtrij naar de andere in één klik.",
  "admin.bulk.source": "Van",
  "admin.bulk.target": "Naar",
  "admin.bulk.allPrivate": "Alle privé tickets in de organisatie",
  "admin.bulk.fromAgent": "Alle tickets van {name}",
  "admin.bulk.onlyPrivate": "Enkel privé tickets",
  "admin.bulk.apply": "Toewijzen",
  "admin.bulk.applying": "Bezig…",
  "admin.bulk.success": "{n} ticket(s) toegewezen.",
  "admin.forms.heading": "Intake-formulieren",
  "admin.forms.intro": "Genereer een publiek ticketformulier. Deel de URL of plak de iframe-snippet op je website.",
  "admin.forms.newButton": "Nieuw formulier",
  "admin.forms.newHeading": "Nieuw intake-formulier",
  "admin.forms.editHeading": "Intake-formulier bewerken",
  "admin.forms.name": "Naam van het formulier",
  "admin.forms.slug": "URL-slug",
  "admin.forms.slugHelp": "Enkel letters, cijfers en streepjes — wordt gebruikt in de publieke URL.",
  "admin.forms.teamMode": "Team toewijzing",
  "admin.forms.modeFixed": "Altijd aan één team toewijzen",
  "admin.forms.modeChoose": "Bezoeker laat een team kiezen",
  "admin.forms.fixedTeam": "Team",
  "admin.forms.selectableTeams": "Teams waar de bezoeker uit kan kiezen",
  "admin.forms.selectableTeamsHelp": "Vink niets aan om alle teams in de organisatie toe te laten.",
  "admin.forms.titleField": "Titel (zichtbaar op het formulier)",
  "admin.forms.titlePlaceholder": "Kop die de bezoeker ziet",
  "admin.forms.titleHelp": "Vervangt de interne naam van het formulier op de publieke pagina. Laat leeg om de naam te blijven tonen.",
  "admin.forms.introText": "Introtekst (optioneel)",
  "admin.forms.successText": "Bevestigingstekst (optioneel)",
  "admin.forms.redirectUrl": "Redirect-URL (optioneel)",
  "admin.forms.redirectUrlPlaceholder": "https://www.voorbeeld.be/bedankt",
  "admin.forms.redirectUrlHelp": "Na een geslaagde verzending wordt de bezoeker naar deze URL gestuurd in plaats van de bevestigingstekst te zien. Leeg laten = bevestigingstekst tonen.",
  "admin.forms.noteHtml": "Kleine letters onder het formulier (HTML toegestaan)",
  "admin.forms.noteHtmlPlaceholder": "Geen spam. Eén bevestiging, één herinnering de dag ervoor.<br>Gebouwd met <a href=\"#\">SiteAI op DotBots Boutique</a>.",
  "admin.forms.noteHtmlHelp": "Verschijnt als grijze kleine alinea onder het formulier. Gebruik <br> voor een nieuwe regel en <a href=\"...\">links</a>.",
  "admin.forms.fixedSubject": "Vast onderwerp (optioneel)",
  "admin.forms.fixedSubjectPlaceholder": "bv. Inschrijving nieuwsbrief",
  "admin.forms.fixedSubjectHelp": "Stel een onderwerp in dat voor elk ticket van dit formulier wordt gebruikt. Het onderwerp-veld wordt verborgen op het publieke formulier zodra dit is ingevuld.",
  "admin.forms.fieldLabelsShow": "Veld-labels & knop aanpassen",
  "admin.forms.fieldLabelsHide": "Veld-labels & knop verbergen",
  "admin.forms.fieldLabelsHelp": "Overschrijf de zichtbare labels en de tekst op de verzendknop voor deze taal. Leeg laten = AI-vertaalde standaard behouden.",
  "admin.forms.fieldLabel.name": "Naam-label",
  "admin.forms.fieldLabel.name.placeholder": "bv. Jouw naam",
  "admin.forms.fieldLabel.email": "E-mail-label",
  "admin.forms.fieldLabel.email.placeholder": "bv. E-mailadres",
  "admin.forms.fieldLabel.subject": "Onderwerp-label",
  "admin.forms.fieldLabel.subject.placeholder": "bv. Onderwerp",
  "admin.forms.fieldLabel.description": "Omschrijving-label",
  "admin.forms.fieldLabel.description.placeholder": "bv. Waarmee kunnen we helpen?",
  "admin.forms.fieldLabel.team": "Team-label",
  "admin.forms.fieldLabel.team.placeholder": "bv. Afdeling",
  "admin.forms.fieldLabel.teamPlaceholder": "Team-placeholder",
  "admin.forms.fieldLabel.teamPlaceholder.placeholder": "bv. Kies een team…",
  "admin.forms.fieldLabel.submit": "Tekst op verzendknop",
  "admin.forms.fieldLabel.submit.placeholder": "bv. Mijn vraag versturen",
  "admin.forms.publicUrl": "Publieke URL",
  "admin.forms.embedJs": "Embed-code (script — aanbevolen)",
  "admin.forms.embedJsHelp": "Plak dit op je website.",
  "admin.forms.embedJsRestyleButton": "Voorbeeld van restyling bekijken",
  "admin.forms.embedJsRestyleHeading": "Embed restylen",
  "admin.forms.embedJsRestyleIntro": "Het formulier gebruikt deze class-namen. Overrule ze in je eigen CSS om de stijl van je site te matchen.",
  "admin.forms.embedJsRestyleSample": "Voorbeeld-CSS — plak dit in je eigen stylesheet en pas aan naar smaak:",
  "admin.forms.open": "Openen",
  "admin.forms.empty": "Nog geen intake-formulieren.",
  "admin.forms.deleteConfirm": "Formulier verwijderen? Tickets die via dit formulier gemaakt zijn blijven bewaard; de publieke URL stopt met werken.",
  "admin.forms.translations": "Vertalingen",
  "admin.forms.addLanguage": "Taal toevoegen",
  "admin.forms.pickLanguage": "Kies een taal…",
  "admin.forms.customLangCode": "of typ een code (bv. zh, pt-BR)",
  "admin.forms.invalidLangCode": "Gebruik een IETF-taalcode: 2-3 letters, eventueel met regio (bv. en, zh, pt-BR).",
  "admin.forms.noLanguages": "Nog geen talen. Voeg er één toe om de formuliertekst in te vullen.",
  "admin.forms.translateAll": "Vertaal vanuit {from} naar de andere talen",
  "admin.forms.translateAllHint": "Roept de AI op om de intro- en bevestigingstekst in deze taal te vertalen naar alle andere toegevoegde talen.",
  "admin.forms.translating": "Bezig met vertalen…",
  "admin.forms.translateEmpty": "Vul eerst een intro of bevestigingstekst in.",
  "admin.forms.translateNoTargets": "Voeg minstens één andere taal toe om naar te vertalen.",
  "admin.forms.translateNoSource": "Vul eerst een andere taal in zodat er iets is om uit te vertalen.",
  "admin.forms.fillFrom": "Vul {target} in vanuit {from}",
  "admin.forms.fillFromHint": "Vertaalt de inhoud uit {from} alleen naar {target} — de andere talen blijven ongewijzigd.",
  "admin.forms.fillFromDisabled": "{target} invullen (nog geen bron)",
  "admin.sla.newButton": "Nieuw beleid",
  "admin.sla.firstResponseMins": "Eerste reactie (min)",
  "admin.sla.resolutionMins": "Oplostijd (min)",
  "admin.sla.deleteConfirm": "Dit SLA-beleid verwijderen? Tickets en teams die het gebruiken houden hun data maar verliezen de link.",

  "admin.detailsToggle": "Details",
  "admin.signature": "E-mailhandtekening",
  "admin.signaturePlaceholder": "— Jan Janssens\nSupport-agent",
  "admin.teamSignatureHelp": "Wordt gebruikt als de agent zelf geen handtekening heeft ingesteld.",
  "admin.userSignatureHelp": "Wordt onder uitgaande mail gezet als deze agent antwoordt. Valt terug op de team-handtekening als hij leeg is.",
  "admin.teamAliases": "Team-e-mailadressen",
  "admin.teamAliasesHelp": "Mails die op deze adressen toekomen worden automatisch aan dit team toegewezen.",
  "admin.userAliases": "Persoonlijke e-mailadressen",
  "admin.userAliasesHelp": "Mails die op deze adressen toekomen worden automatisch aan deze agent toegewezen en zijn voor andere agents onzichtbaar.",
};

const fr: Dict = {
  "appName": "dotSupport",

  "nav.dashboard": "Tableau de bord",
  "nav.tickets": "Tickets",
  "nav.contacts": "Contacts",
  "nav.knowledge": "Base de connaissances",
  "nav.reports": "Rapports IA",
  "reports.title": "Rapports IA",
  "reports.intro": "Décrivez en langage naturel quels tickets vous voulez exporter et ce qui doit figurer dans le fichier. L'IA traduit votre demande en filtre, vous montre les correspondances, puis génère le fichier.",
  "reports.queryLabel": "Que voulez-vous exporter ?",
  "reports.queryHelp": "Exemple : « Résume les réponses à ma campagne mail avec XYZ dans le sujet néerlandais et ZYX dans le français. Inclus l'email, le fil complet et un résumé IA par réponse. »",
  "reports.queryPlaceholder": "Décrivez les tickets à exporter…",
  "reports.formatLabel": "Format de sortie",
  "reports.analyseButton": "Analyser",
  "reports.planning": "Analyse…",
  "reports.previewTitle": "Aperçu",
  "reports.explanationLabel": "Interprétation",
  "reports.matchCount": "Tickets correspondants",
  "reports.cappedAt": "limité à",
  "reports.columns": "Colonnes",
  "reports.colField": "champ",
  "reports.colAi": "IA",
  "reports.estimatedCost": "Coût IA estimé",
  "reports.aiCalls": "appels IA",
  "reports.sampleTitle": "Aperçu (3 premiers)",
  "reports.colSubject": "Sujet",
  "reports.colEmail": "Email",
  "reports.colStatus": "Statut",
  "reports.generateAndDownload": "Générer et télécharger",
  "reports.generating": "Génération en cours…",
  "reports.largeJobWarning": "Cela peut prendre un moment — les résumés sont regroupés, mais le modèle doit traiter chaque ticket.",
  "reports.progress": "{done} sur {total} tickets traités…",
  "reports.cancelled": "La génération du rapport a été annulée.",
  "reports.historyTitle": "Rapports enregistrés",
  "reports.historyEmpty": "Aucun rapport enregistré — le premier rapport généré apparaîtra ici.",
  "reports.histWhen": "Quand",
  "reports.histFormat": "Format",
  "reports.histRows": "Lignes",
  "reports.histQuery": "Demande",
  "reports.histStatus": "Statut",
  "reports.download": "Télécharger",
  "reports.deleteConfirmTitle": "Supprimer le rapport ?",
  "reports.deleteConfirmBody": "Supprimer définitivement « {name} » ? Le fichier ne pourra pas être restauré.",
  "reports.jobStartFailed": "Impossible de démarrer la tâche — le backend est peut-être encore en déploiement. Réessayez dans une minute.",
  "reports.jobStatusUnreadable": "Le serveur ne répond plus à la vérification du statut. Rafraîchissez la page et réessayez.",
  "reports.noAccessTitle": "Pas d'accès",
  "reports.noAccessBody": "Votre rôle n'a pas accès à l'IA. Demandez à un administrateur si vous devez générer des rapports IA.",
  "nav.settings": "Paramètres",

  "language.label": "Langue",
  "intake.name": "Nom",
  "intake.email": "Adresse e-mail",
  "intake.subject": "Sujet",
  "intake.description": "Description",
  "intake.submit": "Créer le ticket",
  "intake.submitting": "Envoi…",
  "intake.captchaRequired": "Veuillez valider le captcha pour continuer.",
  "intake.teamRequired": "Veuillez choisir une équipe.",
  "intake.teamPlaceholder": "Choisir une équipe…",
  "intake.protectedBy": "Protégé par Cloudflare Turnstile.",
  "intake.thanksHeading": "Merci !",
  "intake.thanksDefault": "Votre ticket #{n} a été créé.",
  "intake.thanksReplyHint": "Vous recevrez un e-mail de confirmation. Répondez directement à cet e-mail pour ajouter des informations.",
  "intake.unavailableHeading": "Formulaire temporairement indisponible",
  "intake.captchaMissing": "Le formulaire n'est pas protégé contre les abus et ne peut donc pas être affiché. L'administrateur doit configurer TURNSTILE_SITE_KEY.",
  "intake.errCaptcha": "La vérification du captcha a échoué. Recommencez le défi et renvoyez le formulaire.",
  "intake.errFormNotFound": "Ce formulaire n'est plus disponible. Demandez un lien à jour à votre contact.",
  "intake.errValidation": "Certains champs sont manquants ou invalides. Vérifiez et réessayez.",
  "intake.errRateLimit": "Trop de soumissions depuis cette adresse. Réessayez plus tard.",
  "intake.errGeneric": "Une erreur s'est produite. Réessayez plus tard.",
  "language.en": "Anglais",
  "language.nl": "Néerlandais",
  "language.fr": "Français",
  "language.de": "Allemand",

  "role.admin": "Administrateur",
  "role.agent": "Agent de support",
  "role.viewer": "Lecteur",
  "role.none": "Aucun rôle",

  "common.loading": "Chargement…",
  "common.save": "Enregistrer",
  "common.saving": "Enregistrement…",
  "common.cancel": "Annuler",
  "common.confirm": "Confirmer",
  "common.ok": "OK",
  "common.close": "Fermer",
  "common.add": "Ajouter",
  "common.delete": "Supprimer",
  "common.confirmDelete": "Confirmer la suppression",
  "common.edit": "Modifier",
  "common.search": "Rechercher",
  "common.previous": "Précédent",
  "common.next": "Suivant",
  "common.page": "Page",
  "common.of": "/",
  "common.tryAgain": "Réessayer",
  "common.refresh": "Rafraîchir",
  "common.notFound": "Page introuvable.",
  "common.dash": "—",

  "status.new": "Nouveau",
  "status.working": "En cours",
  "status.waiting_feedback": "Attente du client",
  "status.resolved": "Résolu",
  "status.closed": "Clôturé",
  "status.canceled": "Annulé",

  "priority.critical": "Critique",
  "priority.high": "Haute",
  "priority.medium": "Moyenne",
  "priority.low": "Basse",

  "filter.all": "Tous",
  "filter.status": "Statut",
  "filter.priority": "Priorité",
  "filter.sla": "SLA",
  "filter.sla.breached": "Dépassé",
  "filter.sla.risk": "Imminent",
  "filter.contact": "Contact",
  "filter.allContacts": "Tous les contacts",
  "kanban.move": "Déplacer",
  "kanban.moveTo": "Déplacer vers",

  "sla.expired": "SLA dépassé",
  "sla.minutes": "{n} min",
  "sla.hours": "{n} h",
  "sla.days": "{n} j",
  "time.now": "à l'instant",
  "time.minutesAgo": "il y a {n} min",
  "time.hoursAgo": "il y a {n} h",
  "time.daysAgo": "il y a {n} j",

  "dashboard.title": "Tableau de bord",
  "dashboard.openTickets": "Tickets ouverts",
  "dashboard.slaAtRisk": "SLA à risque",
  "dashboard.slaBreached": "SLA dépassé",
  "dashboard.activeTotal": "Total actif",
  "dashboard.activeTickets": "Tickets actifs",
  "dashboard.noOpenTickets": "Aucun ticket ouvert.",

  "ticket.number": "#",
  "ticket.subject": "Objet",
  "ticket.contact": "Contact",
  "ticket.status": "Statut",
  "ticket.priority": "Priorité",
  "ticket.sla": "SLA",
  "ticket.lastUpdate": "Dernière mise à jour",
  "ticket.assignee": "Assigné à",
  "ticket.team": "Équipe",
  "ticket.tags": "Étiquettes",
  "ticket.created": "Créé",
  "ticket.firstResponse": "1ère réponse",
  "ticket.resolved": "Résolu",
  "ticket.closed": "Clôturé",
  "ticket.reopens": "Rouvert",
  "ticket.csat": "CSAT",
  "ticket.searchPlaceholder": "Objet, contenu…",

  "ticketList.title": "Tickets",
  "ticketList.viewList": "Liste",
  "ticketList.viewKanban": "Kanban",
  "ticketList.viewTeam": "Équipe",
  "ticketList.sortHint": "Cliquez pour trier, recliquez pour inverser, troisième clic pour effacer.",
  "ticketList.bulkSelected": "{n} sélectionnés",
  "ticketList.contactFilter": "Contact :",
  "ticketList.clearContactFilter": "Effacer le filtre contact",
  "ticketList.bulkSelectHint": "Sélectionnez des tickets pour les assigner à une équipe.",
  "ticketList.bulkAssign": "Assigner à l'équipe",
  "ticketList.bulkApplying": "En cours…",
  "ticketList.bulkSetStatus": "Appliquer le statut",
  "ticketList.bulkStatusPlaceholder": "Changer le statut en…",
  "ticketList.bulkStatusAria": "Nouveau statut pour les tickets sélectionnés",
  "ticketList.bulkTeamAria": "Nouvelle équipe pour les tickets sélectionnés",
  "ticketList.bulkStatusResult": "Statut mis à jour sur {n} des {total} ticket(s). {skipped} ignoré(s) car la transition n'était pas autorisée.",
  "ticketList.bulkStatusResultClean": "Statut mis à jour sur {n} des {total} ticket(s).",
  "ticketList.selectAll": "Tout sélectionner",
  "ticketList.selectRow": "Sélectionner",
  "ticketList.kanbanEmpty": "Rien ici.",
  "tickets.privateBadge": "Privé",
  "ticketList.awaitingReply": "En attente de réponse",
  "ticketDetail.awaitingReply": "Le client attend votre réponse",
  "ticketDetail.awaitingReplyClear": "Marquer comme traité — supprimer l'indicateur d'attente",
  "tickets.privateField": "Ticket privé",
  "tickets.privateHelp": "Seul l'agent assigné voit ce ticket.",
  "tickets.inboundAliasField": "Envoyer depuis",
  "tickets.inboundAliasDefault": "Automatique (par défaut)",
  "tickets.inboundAliasPersonalGroup": "Vos adresses",
  "tickets.inboundAliasTeamGroup": "Adresses d'équipe",
  "tickets.inboundAliasHelp": "Les réponses sortantes sur ce ticket partiront de l'adresse choisie.",
  "tickets.inlineRequired": "Veuillez remplir les champs obligatoires.",

  "stats.slaAdherence": "Respect des SLA",
  "stats.slaInBreach": "{n} ticket(s) actuellement en dépassement",
  "stats.firstResponse": "Première réponse",
  "stats.firstResponseHint": "Moyenne sur tous les tickets",
  "stats.resolutionRate": "Taux de résolution",
  "stats.resolvedOf": "{resolved} sur {total} résolus ou clôturés",
  "stats.assignedToYou": "Assignés à vous",
  "stats.openTotal": "{n} ouverts au total",

  "ticketDetail.aiSuggestReply": "Suggestion de réponse IA",
  "ticketDetail.translateToMyLang": "Traduire",
  "ticketDetail.showOriginal": "Afficher l'original",
  "ticketDetail.retranslate": "Retraduire",
  "ticketDetail.translateReplyToTicket": "Traduire la réponse",
  "ticketDetail.translateReplyHint": "Traduit votre réponse vers la langue d'origine du ticket.",
  "ticketDetail.translating": "Traduction…",
  "ticketDetail.aiTranslateFailed": "Échec de la traduction : {message}",
  "ticketDetail.aiThinking": "L'IA réfléchit…",
  "ticketDetail.aiSummary": "Résumé IA",
  "ticketDetail.aiSummaryHeading": "Résumé IA",
  "ticketDetail.kbHeading": "Base de connaissances",
  "ticketDetail.kbHelp": "Transformez cette conversation résolue en article de base de connaissances pour le prochain agent (ou la suggestion IA).",
  "ticketDetail.kbExtract": "Créer un article",
  "ticketDetail.kbExtracting": "Rédaction de l'article…",
  "ticketDetail.kbCreated": "Article créé. Ouvrez-le pour le relire et l'ajuster :",
  "ticketDetail.kbExtractFailed": "Impossible de générer l'article : {message}",
  "ticketDetail.internalNote": "Note interne",
  "ticketDetail.replyPlaceholder": "Réponse au client…",
  "ticketDetail.notePlaceholder": "Note interne (visible uniquement par les agents)…",
  "ticketDetail.send": "Envoyer la réponse",
  "ticketDetail.saveNote": "Enregistrer la note",
  "ticketDetail.sending": "Envoi…",
  "ticketDetail.contact": "Contact",
  "ticketDetail.properties": "Propriétés",
  "ticketDetail.metrics": "Métriques",
  "ticketDetail.noMessages": "Aucun message.",
  "ticketDetail.client": "Client",
  "ticketDetail.agent": "Agent",
  "ticketDetail.unknown": "Inconnu",
  "ticketDetail.noAssignee": "Personne",
  "ticketDetail.deadline": "Échéance",
  "ticketDetail.noSlaPolicy": "Aucune politique SLA",
  "ticketDetail.aiSuggestionFailed": "Échec de la suggestion IA : {message}",
  "ticketDetail.aiSummaryFailed": "Échec du résumé IA : {message}",
  "ticketDetail.archive": "Archiver",
  "ticketDetail.replyShort": "Répondre",
  "ticketDetail.prevTicket": "Précédent",
  "ticketDetail.nextTicket": "Suivant",
  "ticketDetail.sentTo": "Envoyé à",
  "ticketDetail.latestBadge": "Dernier",
  "ticketDetail.attachFiles": "Joindre des fichiers",
  "ticketDetail.attachmentRemove": "Retirer la pièce jointe",
  "ticketDetail.attachmentDownloadFailed": "Téléchargement échoué : {message}",
  "ticketDetail.customerHeading": "Coordonnées client",
  "ticketDetail.editCustomer": "Modifier le client",
  "ticketDetail.expandMessage": "Agrandir le message",
  "ticketDetail.refreshMessage": "Rafraîchir le statut d'envoi",
  "ticketDetail.retryMessage": "Renvoyer",
  "ticketDetail.retryFailed": "Échec du renvoi : {message}",
  "ticketDetail.statsHeading": "Statistiques",

  "contacts.title": "Contacts",
  "contacts.searchPlaceholder": "Nom ou e-mail…",
  "contacts.clearSelection": "Effacer la sélection",
  "contacts.noMatches": "Aucun contact ne correspond",
  "contacts.typeToSearch": "Les 200 premiers sont affichés — tapez pour rechercher tous les contacts.",
  "contacts.tableName": "Nom",
  "contacts.tableEmail": "E-mail",
  "contacts.tablePhone": "Téléphone",
  "contacts.tableType": "Type",
  "contacts.tableTickets": "Tickets",
  "contacts.tableCreated": "Créé",
  "contacts.ticketsHeading": "Tickets",
  "contacts.noTickets": "Aucun ticket.",
  "contacts.viewTicketsHint": "Voir les tickets de ce contact",
  "contacts.viewAllTickets": "Voir tous les tickets de ce contact",

  "knowledge.title": "Base de connaissances",
  "knowledge.searchPlaceholder": "Titre ou contenu…",
  "knowledge.editArticle": "Modifier l'article",
  "knowledge.newArticle": "Nouvel article",
  "knowledge.titleField": "Titre",
  "knowledge.contentField": "Contenu",
  "knowledge.tagsField": "Étiquettes (séparées par virgules)",
  "knowledge.deleteConfirm": "Supprimer cet article ?",
  "knowledge.empty": "Aucun article trouvé.",
  "knowledge.generate.heading": "Générer des articles depuis la documentation produit",
  "knowledge.generate.intro": "Collez de la documentation et laissez l'IA en faire de courts articles en mode question-réponse. Chacun est enregistré et reste modifiable.",
  "knowledge.generate.openButton": "Générer depuis la doc",
  "knowledge.generate.textField": "Documentation produit",
  "knowledge.generate.placeholder": "Collez ici un manuel, une FAQ, des release notes ou un descriptif de fonctionnalité…",
  "knowledge.generate.help": "Visez au moins un paragraphe. L'IA s'en tient à ce qui est écrit — aucune fonctionnalité n'est inventée.",
  "knowledge.generate.generateButton": "Générer les articles",
  "knowledge.generate.generating": "Génération…",
  "knowledge.generate.success": "{n} nouvel·le·s article·s créé·s. Ils figurent déjà dans la liste ci-dessous.",
  "knowledge.generate.tooShort": "Indiquez au moins un paragraphe de documentation.",

  "settings.title": "Paramètres",
  "settings.intro": "Configuration e-mail pour cette organisation. Les secrets sont stockés en base de données et jamais renvoyés au navigateur.",
  "settings.outbound.title": "E-mail sortant",
  "settings.outbound.provider": "Fournisseur",
  "settings.outbound.providerSmtp": "SMTP",
  "settings.outbound.providerResend": "Resend (API HTTP)",
  "settings.outbound.from": "Expéditeur (From)",
  "settings.outbound.fromHelp": "Avec Resend, cette adresse doit appartenir à un domaine vérifié.",
  "settings.outbound.replyTo": "Reply-to (e-mail de support)",
  "settings.outbound.smtpHost": "Hôte SMTP",
  "settings.outbound.smtpPort": "Port SMTP",
  "settings.outbound.smtpUser": "Utilisateur SMTP",
  "settings.outbound.smtpPass": "Mot de passe SMTP",
  "settings.outbound.smtpPassPlaceholder": "Saisissez un mot de passe",
  "settings.outbound.secretSet": "Une valeur est déjà configurée. Laisser vide pour la conserver.",
  "settings.outbound.secretUnset": "Aucun mot de passe défini.",
  "settings.outbound.resendKey": "Clé API Resend",
  "settings.outbound.resendKeySet": "Une clé est déjà configurée. Laisser vide pour la conserver.",
  "settings.outbound.resendKeyUnset": "Générez une clé API dans le tableau de bord Resend.",
  "settings.inbound.title": "E-mail entrant",
  "settings.inbound.method": "Méthode",
  "settings.inbound.methodWebhook": "Webhook (le provider POSTe vers /api/email/inbound)",
  "settings.inbound.methodImap": "IMAP (le serveur interroge la boîte)",
  "settings.inbound.methodBoth": "Les deux",
  "settings.inbound.webhookSecret": "Secret du webhook",
  "settings.inbound.webhookSecretPlaceholder": "Choisissez un secret fort",
  "settings.inbound.webhookExplain": "Le webhook valide l'en-tête X-Webhook-Secret par rapport à cette valeur.",
  "settings.inbound.webhookUrlOrgHint": "Lié à l'organisation {orgId}. L'identifiant d'org dans le chemin rattache explicitement le webhook à cette organisation ; l'en-tête X-Webhook-Secret doit toujours correspondre.",
  "settings.outbound.resendWebhookUrlOrgHint": "Lié à l'organisation {orgId}. L'identifiant d'org dans le chemin rattache explicitement le webhook à cette organisation ; la signature Svix de Resend est vérifiée avec le secret de signature de cette org.",
  "settings.inbound.webhookSecretSet": "Laisser vide pour conserver le secret existant.",
  "settings.inbound.webhookSecretUnset": "Sans secret, le webhook répond 503.",
  "settings.imap.host": "Hôte IMAP",
  "settings.imap.port": "Port IMAP",
  "settings.imap.tls": "Utiliser TLS (port 993)",
  "settings.imap.user": "Utilisateur IMAP",
  "settings.imap.pass": "Mot de passe IMAP",
  "settings.imap.passSet": "Mot de passe déjà défini. Laisser vide pour le conserver.",
  "settings.imap.passUnset": "De nombreux fournisseurs exigent un mot de passe spécifique.",
  "settings.imap.folder": "Dossier",
  "settings.imap.pollSeconds": "Intervalle d'interrogation (secondes)",
  "settings.imap.pollSecondsHelp": "Min 15s, max 3600s. Par défaut 120s.",
  "settings.imap.test": "Tester l'interrogation IMAP",
  "settings.imap.testing": "Interrogation…",
  "settings.imap.testHint": "Lance une interrogation immédiate et rapporte le nombre de messages importés. Utile si des e-mails semblent manquer.",
  "settings.imap.testOk": "Interrogation réussie — {processed} nouveau(x) message(s), dernier UID {lastUid} ({ms} ms).",
  "settings.imap.testFailed": "Échec de l'interrogation : {error}",
  "settings.saved": "Paramètres enregistrés.",
  "settings.noAccess": "Vous n'avez pas accès aux paramètres.",
  "settings.noAccessRoleLabel": "Rôle actuel : {roles}.",
  "settings.noAccessNoRole": "Demandez à un administrateur d'attribuer un rôle à votre compte dans la plateforme DotBots.",
  "settings.noAccessUpgrade": "Demandez à un administrateur de passer votre rôle à Agent ou Administrateur.",
  "settings.readOnly": "Votre rôle ({roles}) ne permet que la lecture. Demandez à un administrateur de modifier les paramètres.",
  "settings.loadFailed": "Impossible de charger les paramètres.",

  "settings.inbound.webhookUrl": "URL du webhook",
  "settings.inbound.payloadHeading": "Payload attendu",
  "settings.inbound.payloadHelp": "Configurez votre fournisseur d'e-mail entrant pour envoyer un POST JSON à cette URL. Envoyez le secret ci-dessus dans l'en-tête X-Webhook-Secret.",
  "settings.outbound.resendWebhookHeading": "Webhook de livraison Resend",
  "settings.outbound.resendWebhookHelp": "Ajoutez cette URL dans Resend → Webhooks. Resend signe chaque appel avec des en-têtes Svix (svix-id / svix-timestamp / svix-signature) ; nous les vérifions et mettons à jour le statut de chaque message.",
  "settings.outbound.resendWebhookUrl": "URL du webhook",
  "settings.outbound.resendWebhookSecret": "Clé de signature Resend (whsec_…)",
  "settings.outbound.resendWebhookSecretSet": "Une clé de signature est déjà configurée. Laisser vide pour la conserver.",
  "settings.outbound.resendWebhookSecretUnset": "Copiez la clé de signature affichée lors de la création du webhook dans Resend.",
  "settings.outbound.resendOnly": "(Utilisé uniquement quand Resend est le fournisseur sortant.)",

  "settings.tabs.outbound": "Sortant",
  "settings.tabs.inbound": "Entrant",
  "settings.tabs.webhooks": "Webhooks",
  "settings.tabs.templates": "Modèles d'e-mails",
  "settings.templates.heading": "Modèles d'e-mails sortants",
  "settings.templates.intro": "Modifiez les e-mails transactionnels envoyés par l'application. Les modèles par défaut sont fournis en anglais, néerlandais, français et allemand ; vous pouvez en ajouter.",
  "settings.templates.defaultLang": "Langue par défaut des e-mails",
  "settings.templates.defaultLangHelp": "Utilisée quand la langue du destinataire n'est pas connue (par exemple, tickets créés via e-mail entrant).",
  "settings.templates.languages": "Langues",
  "settings.templates.addLanguage": "Ajouter une langue",
  "settings.templates.addLanguagePrompt": "Code de langue",
  "settings.templates.addLanguageHelp": "Code IETF de deux ou trois lettres (ex. it, es, zh-CN). L'IA traduit les modèles anglais comme point de départ ; vous pouvez modifier ensuite.",
  "settings.templates.translateAndAdd": "Traduire & ajouter",
  "settings.templates.translating": "Traduction…",
  "settings.templates.removeLangAria": "Retirer {lang}",
  "settings.templates.removeLangConfirm": "Retirer toutes les surcharges pour {lang} ? Les langues intégrées reviendront aux modèles par défaut ; les langues personnalisées disparaîtront de l'éditeur.",
  "settings.templates.langCodeInvalid": "Code de langue non valide. Deux ou trois lettres minuscules, éventuellement avec une région (ex. zh-CN).",
  "settings.templates.langExists": "Cette langue est déjà dans la liste.",
  "settings.templates.langAdded": "Langue ajoutée.",
  "settings.templates.langRemoved": "Langue retirée.",
  "settings.templates.kind.label": "E-mail",
  "settings.templates.kind.confirmation": "Confirmation de ticket",
  "settings.templates.kind.agent_reply": "Réponse d'agent",
  "settings.templates.kind.csat": "Résolu (CSAT)",
  "settings.templates.kind.reopen_ack": "Réouverture confirmée",
  "settings.templates.subject": "Objet",
  "settings.templates.subjectHelp": "Le préfixe [#{ticketNumber}] est ajouté automatiquement — ne l'incluez pas ici.",
  "settings.templates.body": "Corps",
  "settings.templates.placeholdersHelp": "Variables disponibles : {ticketNumber}, {subject}, {body} (réponse d'agent uniquement), {csatLink} (e-mail CSAT uniquement).",
  "settings.templates.resetToDefault": "Réinitialiser",
  "settings.templates.resetConfirm": "Supprimer votre version personnalisée et revenir au modèle par défaut ?",
  "settings.templates.resetFlash": "Réinitialisé.",
  "settings.templates.savedFlash": "Modèle enregistré.",
  "settings.templates.previewShow": "Afficher l'aperçu",
  "settings.templates.previewHide": "Masquer l'aperçu",
  "settings.templates.previewHeading": "Aperçu",
  "settings.templates.previewMeta": "Rendu avec le ticket #{n} et une réponse fictive, mis à jour à mesure que vous tapez.",
  "settings.templates.previewEmpty": "Le corps est vide — tapez quelque chose dans l'éditeur pour voir l'e-mail rendu ici.",
  "settings.templates.defaultUpdated": "Langue par défaut mise à jour.",
  "settings.templates.sourceOverride": "Version personnalisée pour cette langue.",
  "settings.templates.sourceBuiltin": "Modèle par défaut. Enregistrez pour conserver vos modifications comme surcharge.",
  "settings.templates.sourceFallback": "Aucun modèle configuré pour cette langue. Repli sur le modèle intégré {lang}.",
  "settings.summary.outbound": "Sortant",
  "settings.summary.inbound": "Entrant",
  "settings.summary.statusReady": "Prêt",
  "settings.summary.statusIncomplete": "Incomplet",
  "settings.summary.statusEmpty": "Non configuré",
  "settings.payloadShow": "Afficher le payload d'exemple",
  "settings.payloadHide": "Masquer le payload d'exemple",
  "settings.section.smtpCreds": "Identifiants SMTP",
  "settings.section.imapServer": "Serveur IMAP",
  "settings.section.imapAuth": "Authentification IMAP",
  "settings.section.inboundWebhookHeading": "Webhook mail entrant",
  "settings.section.inboundWebhookHelp": "Configurez votre fournisseur d'emails entrants pour POSTer le JSON ici.",

  "common.copy": "Copier",
  "common.copied": "Copié",

  "delivery.queued": "En file",
  "delivery.sent": "Envoyé",
  "delivery.delivered": "Livré",
  "delivery.opened": "Ouvert",
  "delivery.delayed": "Retardé",
  "delivery.bounced": "Rejeté",
  "delivery.complained": "Marqué comme spam",
  "delivery.failed": "Échec",
  "delivery.smtpHint": "SMTP — confirmation de livraison non reportée par le fournisseur.",
  "delivery.errorPrefix": "Erreur",

  "common.create": "Créer",
  "common.creating": "Création…",
  "common.firstName": "Prénom",
  "common.lastName": "Nom",
  "common.email": "E-mail",
  "common.phone": "Téléphone",
  "common.required": "obligatoire",

  "contacts.newButton": "Nouveau contact",
  "contacts.backToList": "Retour aux contacts",
  "contacts.newHeading": "Nouveau contact",
  "contacts.customerType": "Type",
  "contacts.customerTypeB2B": "Professionnel (B2B)",
  "contacts.customerTypeB2C": "Particulier (B2C)",
  "contacts.customerTypeVIP": "VIP",
  "contacts.consentEmail": "Autoriser le contact par e-mail",
  "contacts.consentMarketing": "Autoriser les e-mails marketing",
  "contacts.created": "Contact créé.",
  "contacts.updated": "Contact mis à jour.",
  "contacts.editButton": "Modifier le contact",
  "contacts.editHeading": "Modifier le contact",

  "tickets.newButton": "Nouveau ticket",
  "tickets.tabMine": "Mes tickets",
  "tickets.tabAll": "Tous",
  "tickets.tabNoTeam": "Sans équipe",
  "tickets.newHeading": "Nouveau ticket",
  "tickets.contactField": "Contact",
  "tickets.contactPlaceholder": "Choisir un contact…",
  "tickets.contactRequired": "Choisissez d'abord un contact. Créez-en un via la page Contacts si nécessaire.",
  "tickets.bodyField": "Description",
  "tickets.bodyPlaceholder": "Que demande le client ?",
  "tickets.tagsField": "Étiquettes (séparées par virgules)",
  "tickets.unassigned": "Non assigné",
  "tickets.noTeam": "Aucune équipe",
  "tickets.noSla": "Aucune SLA",

  "nav.admin": "Admin",
  "nav.autoArchive": "Auto-archive",
  "autoArchive.title": "Règles d'auto-archivage",
  "autoArchive.intro": "Les mails entrants qui correspondent à une de ces règles sont transformés en ticket et clôturés aussitôt — ils restent cherchables mais sortent de la file d'attente.",
  "autoArchive.addTitle": "Ajouter une règle",
  "autoArchive.addButton": "Ajouter la règle",
  "autoArchive.listTitle": "Règles existantes",
  "autoArchive.empty": "Aucune règle pour l'instant.",
  "autoArchive.noAccess": "Il faut un rôle admin pour gérer les règles d'auto-archivage.",
  "autoArchive.kindLabel": "Correspond sur",
  "autoArchive.patternLabel": "Motif",
  "autoArchive.noteLabel": "Note (facultative)",
  "autoArchive.notePlaceholder": "ex. reçus Stripe, résumé stories Instagram",
  "autoArchive.noteFromTicket": "Ajouté depuis le ticket {ticketId}",
  "autoArchive.colKind": "Correspond sur",
  "autoArchive.colPattern": "Motif",
  "autoArchive.colNote": "Note",
  "autoArchive.deleteConfirmTitle": "Supprimer la règle ?",
  "autoArchive.deleteConfirmBody": "Supprimer la règle d'auto-archivage pour « {pattern} » ? Les prochains mails correspondants reviendront dans la file.",
  "autoArchive.addFromTicketTitle": "Créer une règle d'auto-archivage depuis ce ticket",
  "autoArchive.addFromTicketShort": "Auto-archiver ce type",
  "autoArchive.addFromTicketBody": "Tout mail entrant correspondant à cette règle sera transformé en ticket clôturé.",
  "autoArchive.alsoCloseThisTicket": "Clôturer aussi ce ticket maintenant",
  "autoArchive.kind.from_domain": "Domaine expéditeur",
  "autoArchive.kind.from_address": "Adresse expéditeur (exacte)",
  "autoArchive.kind.subject_contains": "Objet contient",
  "autoArchive.help.from_domain": "Correspond si l'adresse d'expéditeur se termine par ce domaine (ex. « mail.instagram.com » attrape « x@mail.instagram.com »).",
  "autoArchive.help.from_address": "Correspond uniquement à cette adresse exacte.",
  "autoArchive.help.subject_contains": "Correspond si l'objet contient ce fragment de texte, insensible à la casse.",
  "autoArchive.applyToInbox": "Appliquer aussi aux tickets déjà présents dans la boîte",
  "autoArchive.applyToInboxHelp": "Les tickets ouverts dont l'expéditeur ou l'objet correspond sont également fermés. Les tickets déjà fermés restent inchangés.",
  "autoArchive.applyRow": "Appliquer à la boîte",
  "autoArchive.applying": "En cours…",
  "autoArchive.sweepResult": "{closed} tickets ouverts fermés sur {scanned}.",
  "autoArchive.sweepRowResult": "{closed}/{scanned} fermés",
  "autoArchive.duplicateError": "Une règle avec ce motif existe déjà.",
  "admin.title": "Équipes & personnes",
  "admin.intro": "Équipes, agents et politiques SLA de cette organisation. Les équipes et personnes provenant de la plateforme DotBots sont marquées comme telles.",
  "admin.platformBadge": "Plateforme",
  "admin.manualBadge": "Manuel",
  "admin.sync": "Synchroniser avec la plateforme",
  "admin.syncing": "Synchronisation…",
  "admin.syncIntro": "Récupère votre compte et vos équipes depuis DotBots Boutique. Les autres agents sont importés automatiquement à leur première connexion.",
  "admin.syncSuccess": "{count} équipe(s) synchronisée(s) depuis la plateforme.",
  "admin.syncSuccessJwt": "Synchronisé via le JWT (SDK plateforme indisponible).",

  "admin.teams.heading": "Équipes",
  "admin.teams.newButton": "Nouvelle équipe",
  "admin.teams.name": "Nom",
  "admin.teams.defaultSla": "SLA par défaut",
  "admin.teams.source": "Source",
  "admin.teams.deleteConfirm": "Supprimer cette équipe ? Les tickets et agents associés perdront leur lien.",

  "admin.agents.heading": "Agents",
  "admin.agents.newButton": "Nouvel agent",
  "admin.agents.team": "Équipe",
  "admin.agents.platformUserId": "Identifiant utilisateur plateforme",
  "admin.agents.deleteConfirm": "Supprimer cet agent ? Ses tickets perdront leur assignation.",

  "admin.sla.heading": "Politiques SLA",
  "admin.private.heading": "Tickets privés",
  "admin.private.intro": "Tickets privés ouverts dans votre organisation. Le contenu et le contact restent masqués — les administrateurs peuvent uniquement les réassigner.",
  "admin.private.reassign": "Réassigner",
  "admin.private.pickAgent": "Choisir un agent…",
  "admin.private.empty": "Aucun ticket privé ouvert.",
  "admin.bulk.heading": "Réassignation en masse",
  "admin.bulk.intro": "Déplacez chaque ticket ouvert d'une file à une autre en un clic.",
  "admin.bulk.source": "De",
  "admin.bulk.target": "Vers",
  "admin.bulk.allPrivate": "Tous les tickets privés de l'organisation",
  "admin.bulk.fromAgent": "Tous les tickets de {name}",
  "admin.bulk.onlyPrivate": "Tickets privés uniquement",
  "admin.bulk.apply": "Réassigner",
  "admin.bulk.applying": "En cours…",
  "admin.bulk.success": "{n} ticket(s) réassigné(s).",
  "admin.forms.heading": "Formulaires d'intake",
  "admin.forms.intro": "Générez un formulaire de ticket public. Partagez l'URL ou collez l'iframe sur votre site.",
  "admin.forms.newButton": "Nouveau formulaire",
  "admin.forms.newHeading": "Nouveau formulaire d'intake",
  "admin.forms.editHeading": "Modifier le formulaire",
  "admin.forms.name": "Nom du formulaire",
  "admin.forms.slug": "Slug d'URL",
  "admin.forms.slugHelp": "Lettres, chiffres et tirets uniquement — utilisé dans l'URL publique.",
  "admin.forms.teamMode": "Attribution d'équipe",
  "admin.forms.modeFixed": "Toujours assigner à une équipe",
  "admin.forms.modeChoose": "Laisser le visiteur choisir une équipe",
  "admin.forms.fixedTeam": "Équipe",
  "admin.forms.selectableTeams": "Équipes que le visiteur peut choisir",
  "admin.forms.selectableTeamsHelp": "Laissez tout décoché pour autoriser toutes les équipes.",
  "admin.forms.titleField": "Titre (affiché sur le formulaire)",
  "admin.forms.titlePlaceholder": "En-tête vu par le visiteur",
  "admin.forms.titleHelp": "Remplace le nom interne du formulaire sur la page publique. Laissez vide pour conserver le nom.",
  "admin.forms.introText": "Texte d'introduction (optionnel)",
  "admin.forms.successText": "Texte de confirmation (optionnel)",
  "admin.forms.redirectUrl": "URL de redirection (optionnel)",
  "admin.forms.redirectUrlPlaceholder": "https://www.exemple.com/merci",
  "admin.forms.redirectUrlHelp": "Après un envoi réussi, redirigez les visiteurs vers cette URL au lieu d'afficher le texte de confirmation. Laissez vide pour garder le texte.",
  "admin.forms.noteHtml": "Petite mention sous le formulaire (HTML autorisé)",
  "admin.forms.noteHtmlPlaceholder": "Pas de spam. Une confirmation, un rappel la veille.<br>Construit avec <a href=\"#\">SiteAI sur DotBots Boutique</a>.",
  "admin.forms.noteHtmlHelp": "Affiché en petit texte gris sous le formulaire. Utilisez <br> pour un retour à la ligne et <a href=\"...\">liens</a>.",
  "admin.forms.fixedSubject": "Sujet fixe (optionnel)",
  "admin.forms.fixedSubjectPlaceholder": "ex. Inscription newsletter",
  "admin.forms.fixedSubjectHelp": "Définissez un sujet utilisé pour chaque ticket envoyé via ce formulaire. Le champ Sujet est masqué sur le formulaire public lorsque celui-ci est rempli.",
  "admin.forms.fieldLabelsShow": "Personnaliser les libellés & le bouton",
  "admin.forms.fieldLabelsHide": "Masquer les libellés & le bouton",
  "admin.forms.fieldLabelsHelp": "Remplacez les libellés visibles et le texte du bouton d'envoi pour cette langue. Laissez vide pour conserver la traduction IA par défaut.",
  "admin.forms.fieldLabel.name": "Libellé Nom",
  "admin.forms.fieldLabel.name.placeholder": "ex. Votre nom",
  "admin.forms.fieldLabel.email": "Libellé E-mail",
  "admin.forms.fieldLabel.email.placeholder": "ex. Adresse e-mail",
  "admin.forms.fieldLabel.subject": "Libellé Sujet",
  "admin.forms.fieldLabel.subject.placeholder": "ex. Sujet",
  "admin.forms.fieldLabel.description": "Libellé Description",
  "admin.forms.fieldLabel.description.placeholder": "ex. Comment pouvons-nous aider ?",
  "admin.forms.fieldLabel.team": "Libellé Équipe",
  "admin.forms.fieldLabel.team.placeholder": "ex. Service",
  "admin.forms.fieldLabel.teamPlaceholder": "Placeholder équipe",
  "admin.forms.fieldLabel.teamPlaceholder.placeholder": "ex. Choisir une équipe…",
  "admin.forms.fieldLabel.submit": "Texte du bouton d'envoi",
  "admin.forms.fieldLabel.submit.placeholder": "ex. Envoyer ma demande",
  "admin.forms.publicUrl": "URL publique",
  "admin.forms.embedJs": "Code à intégrer (script — recommandé)",
  "admin.forms.embedJsHelp": "Collez ceci sur votre site.",
  "admin.forms.embedJsRestyleButton": "Voir un exemple de restyle",
  "admin.forms.embedJsRestyleHeading": "Restyler l'intégration",
  "admin.forms.embedJsRestyleIntro": "Le formulaire utilise ces classes. Surchargez-les dans votre propre CSS pour épouser le style de votre site.",
  "admin.forms.embedJsRestyleSample": "Exemple de CSS — collez dans votre feuille de style et ajustez :",
  "admin.forms.open": "Ouvrir",
  "admin.forms.empty": "Aucun formulaire d'intake.",
  "admin.forms.deleteConfirm": "Supprimer ce formulaire ? Les tickets déjà créés sont conservés ; l'URL publique cesse de fonctionner.",
  "admin.forms.translations": "Traductions",
  "admin.forms.addLanguage": "Ajouter une langue",
  "admin.forms.pickLanguage": "Choisir une langue…",
  "admin.forms.customLangCode": "ou tapez un code (ex. zh, pt-BR)",
  "admin.forms.invalidLangCode": "Utilisez un code IETF : 2-3 lettres, optionnellement une région (ex. en, zh, pt-BR).",
  "admin.forms.noLanguages": "Aucune langue. Ajoutez-en une pour saisir le contenu du formulaire.",
  "admin.forms.translateAll": "Traduire depuis {from} vers les autres langues",
  "admin.forms.translateAllHint": "Demande à l'IA de traduire le texte d'intro et de confirmation de cette langue vers toutes les autres langues ajoutées.",
  "admin.forms.translating": "Traduction…",
  "admin.forms.translateEmpty": "Remplissez d'abord le texte d'intro ou de confirmation.",
  "admin.forms.translateNoTargets": "Ajoutez au moins une autre langue pour traduire.",
  "admin.forms.translateNoSource": "Remplissez d'abord une autre langue pour fournir une source.",
  "admin.forms.fillFrom": "Remplir {target} depuis {from}",
  "admin.forms.fillFromHint": "Traduit le contenu {from} uniquement vers {target} — les autres langues restent inchangées.",
  "admin.forms.fillFromDisabled": "Remplir {target} (aucune source)",
  "admin.sla.newButton": "Nouvelle politique",
  "admin.sla.firstResponseMins": "Première réponse (min)",
  "admin.sla.resolutionMins": "Résolution (min)",
  "admin.sla.deleteConfirm": "Supprimer cette politique SLA ? Les tickets et équipes l'utilisant conservent leurs données mais perdent le lien.",

  "admin.detailsToggle": "Détails",
  "admin.signature": "Signature e-mail",
  "admin.signaturePlaceholder": "— Jean Dupont\nAgent de support",
  "admin.teamSignatureHelp": "Utilisée comme repli quand l'agent n'a pas sa propre signature.",
  "admin.userSignatureHelp": "Ajoutée aux e-mails sortants quand cet agent répond. Repli sur la signature de l'équipe si vide.",
  "admin.teamAliases": "Adresses e-mail de l'équipe",
  "admin.teamAliasesHelp": "Les e-mails reçus sur ces adresses sont automatiquement assignés à cette équipe.",
  "admin.userAliases": "Adresses e-mail personnelles",
  "admin.userAliasesHelp": "Les e-mails reçus sur ces adresses sont auto-assignés à cet agent et deviennent invisibles pour les autres agents.",
};

const de: Dict = {
  "appName": "dotSupport",

  "nav.dashboard": "Übersicht",
  "nav.tickets": "Tickets",
  "nav.contacts": "Kontakte",
  "nav.knowledge": "Wissensdatenbank",
  "nav.reports": "KI-Berichte",
  "reports.title": "KI-Berichte",
  "reports.intro": "Beschreiben Sie in natürlicher Sprache, welche Tickets Sie exportieren möchten und was in die Datei kommt. Die KI übersetzt Ihre Anfrage in einen Filter, zeigt die Treffer und generiert erst dann die Datei.",
  "reports.queryLabel": "Was möchten Sie exportieren?",
  "reports.queryHelp": "Beispiel: „Fasse die Antworten auf meine Kampagnenmail mit XYZ im niederländischen und ZYX im französischen Betreff zusammen. Nimm E-Mail, den vollständigen Thread und eine KI-Zusammenfassung pro Antwort auf.“",
  "reports.queryPlaceholder": "Beschreiben Sie die zu exportierenden Tickets…",
  "reports.formatLabel": "Ausgabeformat",
  "reports.analyseButton": "Analysieren",
  "reports.planning": "Analysiere…",
  "reports.previewTitle": "Vorschau",
  "reports.explanationLabel": "Interpretation",
  "reports.matchCount": "Passende Tickets",
  "reports.cappedAt": "begrenzt auf",
  "reports.columns": "Spalten",
  "reports.colField": "Feld",
  "reports.colAi": "KI",
  "reports.estimatedCost": "Geschätzte KI-Kosten",
  "reports.aiCalls": "KI-Aufrufe",
  "reports.sampleTitle": "Stichprobe (erste 3)",
  "reports.colSubject": "Betreff",
  "reports.colEmail": "E-Mail",
  "reports.colStatus": "Status",
  "reports.generateAndDownload": "Erstellen & herunterladen",
  "reports.generating": "Wird erstellt…",
  "reports.largeJobWarning": "Kann einen Moment dauern — Zusammenfassungen laufen im Batch, aber jedes Ticket muss verarbeitet werden.",
  "reports.progress": "{done} von {total} Tickets verarbeitet…",
  "reports.cancelled": "Die Berichterstellung wurde abgebrochen.",
  "reports.historyTitle": "Gespeicherte Berichte",
  "reports.historyEmpty": "Noch keine Berichte gespeichert — der erste generierte Bericht erscheint hier.",
  "reports.histWhen": "Wann",
  "reports.histFormat": "Format",
  "reports.histRows": "Zeilen",
  "reports.histQuery": "Anfrage",
  "reports.histStatus": "Status",
  "reports.download": "Herunterladen",
  "reports.deleteConfirmTitle": "Bericht löschen?",
  "reports.deleteConfirmBody": "„{name}“ dauerhaft entfernen? Die Datei kann nicht wiederhergestellt werden.",
  "reports.jobStartFailed": "Der Berichts-Job konnte nicht gestartet werden — das Backend wird eventuell noch bereitgestellt. Bitte in einer Minute nochmal versuchen.",
  "reports.jobStatusUnreadable": "Der Server antwortet nicht mehr auf die Statusabfrage. Bitte Seite neu laden und erneut versuchen.",
  "reports.noAccessTitle": "Kein Zugriff",
  "reports.noAccessBody": "Ihre Rolle hat keinen KI-Zugriff. Bitten Sie einen Administrator um Freigabe, wenn Sie KI-Berichte erstellen möchten.",
  "nav.settings": "Einstellungen",

  "language.label": "Sprache",
  "intake.name": "Name",
  "intake.email": "E-Mail-Adresse",
  "intake.subject": "Betreff",
  "intake.description": "Beschreibung",
  "intake.submit": "Ticket erstellen",
  "intake.submitting": "Wird gesendet…",
  "intake.captchaRequired": "Bitte das Captcha bestätigen, um fortzufahren.",
  "intake.teamRequired": "Bitte ein Team wählen.",
  "intake.teamPlaceholder": "Team wählen…",
  "intake.protectedBy": "Geschützt durch Cloudflare Turnstile.",
  "intake.thanksHeading": "Danke!",
  "intake.thanksDefault": "Ihr Ticket #{n} wurde erstellt.",
  "intake.thanksReplyHint": "Sie erhalten eine Bestätigungsmail. Antworten Sie direkt auf diese Mail, um zusätzliche Informationen mitzuschicken.",
  "intake.unavailableHeading": "Formular zurzeit nicht verfügbar",
  "intake.captchaMissing": "Das Formular ist nicht gegen Missbrauch geschützt und kann daher nicht angezeigt werden. Der Administrator muss TURNSTILE_SITE_KEY konfigurieren.",
  "intake.errCaptcha": "Captcha-Prüfung fehlgeschlagen. Bitte das Captcha erneut lösen und das Formular noch einmal absenden.",
  "intake.errFormNotFound": "Dieses Formular ist nicht mehr verfügbar. Bitte fordere einen aktuellen Link bei deinem Ansprechpartner an.",
  "intake.errValidation": "Einige Felder fehlen oder sind ungültig. Bitte prüfen und erneut versuchen.",
  "intake.errRateLimit": "Zu viele Einsendungen von dieser Adresse. Bitte später erneut versuchen.",
  "intake.errGeneric": "Etwas ist schiefgelaufen. Bitte später erneut versuchen.",
  "language.en": "Englisch",
  "language.nl": "Niederländisch",
  "language.fr": "Französisch",
  "language.de": "Deutsch",

  "role.admin": "Administrator",
  "role.agent": "Support-Agent",
  "role.viewer": "Betrachter",
  "role.none": "Keine Rolle",

  "common.loading": "Lädt…",
  "common.save": "Speichern",
  "common.saving": "Speichern…",
  "common.cancel": "Abbrechen",
  "common.confirm": "Bestätigen",
  "common.ok": "OK",
  "common.close": "Schließen",
  "common.add": "Hinzufügen",
  "common.delete": "Löschen",
  "common.confirmDelete": "Löschen bestätigen",
  "common.edit": "Bearbeiten",
  "common.search": "Suchen",
  "common.previous": "Zurück",
  "common.next": "Weiter",
  "common.page": "Seite",
  "common.of": "/",
  "common.tryAgain": "Erneut versuchen",
  "common.refresh": "Aktualisieren",
  "common.notFound": "Seite nicht gefunden.",
  "common.dash": "—",

  "status.new": "Neu",
  "status.working": "In Bearbeitung",
  "status.waiting_feedback": "Wartet auf Kunde",
  "status.resolved": "Gelöst",
  "status.closed": "Geschlossen",
  "status.canceled": "Abgebrochen",

  "priority.critical": "Kritisch",
  "priority.high": "Hoch",
  "priority.medium": "Mittel",
  "priority.low": "Niedrig",

  "filter.all": "Alle",
  "filter.status": "Status",
  "filter.priority": "Priorität",
  "filter.sla": "SLA",
  "filter.sla.breached": "Überschritten",
  "filter.sla.risk": "Bald fällig",
  "filter.contact": "Kontakt",
  "filter.allContacts": "Alle Kontakte",
  "kanban.move": "Verschieben",
  "kanban.moveTo": "Verschieben nach",

  "sla.expired": "SLA überschritten",
  "sla.minutes": "{n} Min.",
  "sla.hours": "{n} Std.",
  "sla.days": "{n} T.",
  "time.now": "gerade eben",
  "time.minutesAgo": "vor {n} Min.",
  "time.hoursAgo": "vor {n} Std.",
  "time.daysAgo": "vor {n} T.",

  "dashboard.title": "Übersicht",
  "dashboard.openTickets": "Offene Tickets",
  "dashboard.slaAtRisk": "SLA gefährdet",
  "dashboard.slaBreached": "SLA überschritten",
  "dashboard.activeTotal": "Aktiv gesamt",
  "dashboard.activeTickets": "Aktive Tickets",
  "dashboard.noOpenTickets": "Keine offenen Tickets.",

  "ticket.number": "#",
  "ticket.subject": "Betreff",
  "ticket.contact": "Kontakt",
  "ticket.status": "Status",
  "ticket.priority": "Priorität",
  "ticket.sla": "SLA",
  "ticket.lastUpdate": "Letzte Aktualisierung",
  "ticket.assignee": "Zugewiesen an",
  "ticket.team": "Team",
  "ticket.tags": "Tags",
  "ticket.created": "Erstellt",
  "ticket.firstResponse": "1. Antwort",
  "ticket.resolved": "Gelöst",
  "ticket.closed": "Geschlossen",
  "ticket.reopens": "Erneut geöffnet",
  "ticket.csat": "CSAT",
  "ticket.searchPlaceholder": "Betreff, Inhalt…",

  "ticketList.title": "Tickets",
  "ticketList.viewList": "Liste",
  "ticketList.viewKanban": "Kanban",
  "ticketList.viewTeam": "Team",
  "ticketList.sortHint": "Klicken zum Sortieren, erneut klicken zum Umkehren, dritter Klick löscht.",
  "ticketList.bulkSelected": "{n} ausgewählt",
  "ticketList.contactFilter": "Kontakt:",
  "ticketList.clearContactFilter": "Kontaktfilter entfernen",
  "ticketList.bulkSelectHint": "Tickets auswählen, um sie einem Team zuzuweisen.",
  "ticketList.bulkAssign": "Team zuweisen",
  "ticketList.bulkApplying": "Zuweisung läuft…",
  "ticketList.bulkSetStatus": "Status anwenden",
  "ticketList.bulkStatusPlaceholder": "Status ändern zu…",
  "ticketList.bulkStatusAria": "Neuer Status für ausgewählte Tickets",
  "ticketList.bulkTeamAria": "Neues Team für ausgewählte Tickets",
  "ticketList.bulkStatusResult": "Status auf {n} von {total} Ticket(s) aktualisiert. {skipped} übersprungen, weil der Übergang nicht erlaubt war.",
  "ticketList.bulkStatusResultClean": "Status auf {n} von {total} Ticket(s) aktualisiert.",
  "ticketList.selectAll": "Alle auswählen",
  "ticketList.selectRow": "Auswählen",
  "ticketList.kanbanEmpty": "Nichts hier.",
  "tickets.privateBadge": "Privat",
  "ticketList.awaitingReply": "Antwort erwartet",
  "ticketDetail.awaitingReply": "Kunde wartet auf Ihre Antwort",
  "ticketDetail.awaitingReplyClear": "Als erledigt markieren — Antwort-Erwartungshinweis entfernen",
  "tickets.privateField": "Privates Ticket",
  "tickets.privateHelp": "Nur die zugewiesene Person sieht dieses Ticket.",
  "tickets.inboundAliasField": "Antworten von",
  "tickets.inboundAliasDefault": "Automatisch (Standard)",
  "tickets.inboundAliasPersonalGroup": "Ihre Adressen",
  "tickets.inboundAliasTeamGroup": "Team-Adressen",
  "tickets.inboundAliasHelp": "Ausgehende Antworten zu diesem Ticket werden von der gewählten Adresse gesendet.",
  "tickets.inlineRequired": "Bitte die Pflichtfelder ausfüllen.",

  "stats.slaAdherence": "SLA-Einhaltung",
  "stats.slaInBreach": "{n} Ticket(s) derzeit überschritten",
  "stats.firstResponse": "Erste Antwort",
  "stats.firstResponseHint": "Durchschnitt über alle Tickets",
  "stats.resolutionRate": "Lösungsquote",
  "stats.resolvedOf": "{resolved} von {total} gelöst oder geschlossen",
  "stats.assignedToYou": "Dir zugewiesen",
  "stats.openTotal": "{n} offen insgesamt",

  "ticketDetail.aiSuggestReply": "KI-Antwortvorschlag",
  "ticketDetail.translateToMyLang": "Übersetzen",
  "ticketDetail.showOriginal": "Original anzeigen",
  "ticketDetail.retranslate": "Erneut übersetzen",
  "ticketDetail.translateReplyToTicket": "Antwort übersetzen",
  "ticketDetail.translateReplyHint": "Übersetzt deine Antwort zurück in die Ursprungssprache des Tickets.",
  "ticketDetail.translating": "Übersetze…",
  "ticketDetail.aiTranslateFailed": "Übersetzung fehlgeschlagen: {message}",
  "ticketDetail.aiThinking": "KI denkt nach…",
  "ticketDetail.aiSummary": "KI-Zusammenfassung",
  "ticketDetail.aiSummaryHeading": "KI-Zusammenfassung",
  "ticketDetail.kbHeading": "Wissensdatenbank",
  "ticketDetail.kbHelp": "Mache aus diesem gelösten Ticket einen Wissensartikel, damit der nächste Agent (oder die KI-Vorschläge) ihn nutzen können.",
  "ticketDetail.kbExtract": "Wissensartikel erstellen",
  "ticketDetail.kbExtracting": "Artikel wird erstellt…",
  "ticketDetail.kbCreated": "Artikel erstellt. Öffnen, um zu prüfen und zu bearbeiten:",
  "ticketDetail.kbExtractFailed": "Wissensartikel konnte nicht erzeugt werden: {message}",
  "ticketDetail.internalNote": "Interne Notiz",
  "ticketDetail.replyPlaceholder": "Antwort an Kunde…",
  "ticketDetail.notePlaceholder": "Interne Notiz (nur für Agents sichtbar)…",
  "ticketDetail.send": "Antwort senden",
  "ticketDetail.saveNote": "Notiz speichern",
  "ticketDetail.sending": "Senden…",
  "ticketDetail.contact": "Kontakt",
  "ticketDetail.properties": "Eigenschaften",
  "ticketDetail.metrics": "Kennzahlen",
  "ticketDetail.noMessages": "Noch keine Nachrichten.",
  "ticketDetail.client": "Kunde",
  "ticketDetail.agent": "Agent",
  "ticketDetail.unknown": "Unbekannt",
  "ticketDetail.noAssignee": "Niemand",
  "ticketDetail.deadline": "Fällig am",
  "ticketDetail.noSlaPolicy": "Keine SLA-Richtlinie",
  "ticketDetail.aiSuggestionFailed": "KI-Vorschlag fehlgeschlagen: {message}",
  "ticketDetail.aiSummaryFailed": "KI-Zusammenfassung fehlgeschlagen: {message}",
  "ticketDetail.archive": "Archivieren",
  "ticketDetail.replyShort": "Antworten",
  "ticketDetail.prevTicket": "Vorheriges",
  "ticketDetail.nextTicket": "Nächstes",
  "ticketDetail.sentTo": "Gesendet an",
  "ticketDetail.latestBadge": "Neueste",
  "ticketDetail.attachFiles": "Dateien anhängen",
  "ticketDetail.attachmentRemove": "Anhang entfernen",
  "ticketDetail.attachmentDownloadFailed": "Download fehlgeschlagen: {message}",
  "ticketDetail.customerHeading": "Kundendaten",
  "ticketDetail.editCustomer": "Kunden bearbeiten",
  "ticketDetail.expandMessage": "Nachricht vergrößern",
  "ticketDetail.refreshMessage": "Zustellstatus aktualisieren",
  "ticketDetail.retryMessage": "Erneut senden",
  "ticketDetail.retryFailed": "Erneutes Senden fehlgeschlagen: {message}",
  "ticketDetail.statsHeading": "Statistiken",

  "contacts.title": "Kontakte",
  "contacts.searchPlaceholder": "Name oder E-Mail…",
  "contacts.clearSelection": "Auswahl löschen",
  "contacts.noMatches": "Keine passenden Kontakte",
  "contacts.typeToSearch": "Nur die ersten 200 werden angezeigt — bitte tippen, um alle Kontakte zu durchsuchen.",
  "contacts.tableName": "Name",
  "contacts.tableEmail": "E-Mail",
  "contacts.tablePhone": "Telefon",
  "contacts.tableType": "Typ",
  "contacts.tableTickets": "Tickets",
  "contacts.tableCreated": "Erstellt",
  "contacts.ticketsHeading": "Tickets",
  "contacts.noTickets": "Noch keine Tickets.",
  "contacts.viewTicketsHint": "Tickets dieses Kontakts anzeigen",
  "contacts.viewAllTickets": "Alle Tickets dieses Kontakts anzeigen",

  "knowledge.title": "Wissensdatenbank",
  "knowledge.searchPlaceholder": "Titel oder Inhalt…",
  "knowledge.editArticle": "Artikel bearbeiten",
  "knowledge.newArticle": "Neuer Artikel",
  "knowledge.titleField": "Titel",
  "knowledge.contentField": "Inhalt",
  "knowledge.tagsField": "Tags (kommagetrennt)",
  "knowledge.deleteConfirm": "Artikel löschen?",
  "knowledge.empty": "Keine Artikel gefunden.",
  "knowledge.generate.heading": "Artikel aus Produktdokumentation erzeugen",
  "knowledge.generate.intro": "Füge Dokumentation ein und lass die KI daraus kurze Frage-Antwort-Artikel machen. Jeder wird gespeichert und kann anschließend bearbeitet werden.",
  "knowledge.generate.openButton": "Aus Doku generieren",
  "knowledge.generate.textField": "Produktdokumentation",
  "knowledge.generate.placeholder": "Hier ein Handbuch, eine FAQ, Release Notes oder eine Feature-Beschreibung einfügen…",
  "knowledge.generate.help": "Mindestens ein Absatz. Die KI bleibt bei dem, was im Text steht — es werden keine Features erfunden.",
  "knowledge.generate.generateButton": "Artikel erzeugen",
  "knowledge.generate.generating": "Wird erzeugt…",
  "knowledge.generate.success": "{n} neue Artikel erstellt. Sie stehen bereits in der Liste unten.",
  "knowledge.generate.tooShort": "Bitte mindestens einen Absatz Dokumentation angeben.",

  "settings.title": "Einstellungen",
  "settings.intro": "E-Mail-Konfiguration für diese Organisation. Geheimnisse werden in der Datenbank gespeichert und nie an den Browser zurückgegeben.",
  "settings.outbound.title": "Ausgehende E-Mail",
  "settings.outbound.provider": "Anbieter",
  "settings.outbound.providerSmtp": "SMTP",
  "settings.outbound.providerResend": "Resend (HTTP API)",
  "settings.outbound.from": "Absender (From)",
  "settings.outbound.fromHelp": "Bei Resend muss diese Adresse zu einer verifizierten Domain gehören.",
  "settings.outbound.replyTo": "Reply-to (Support-E-Mail)",
  "settings.outbound.smtpHost": "SMTP-Host",
  "settings.outbound.smtpPort": "SMTP-Port",
  "settings.outbound.smtpUser": "SMTP-Benutzer",
  "settings.outbound.smtpPass": "SMTP-Passwort",
  "settings.outbound.smtpPassPlaceholder": "Passwort eingeben",
  "settings.outbound.secretSet": "Ein Wert ist bereits hinterlegt. Leer lassen, um ihn beizubehalten.",
  "settings.outbound.secretUnset": "Noch kein Passwort hinterlegt.",
  "settings.outbound.resendKey": "Resend-API-Schlüssel",
  "settings.outbound.resendKeySet": "Ein Schlüssel ist bereits hinterlegt. Leer lassen, um ihn beizubehalten.",
  "settings.outbound.resendKeyUnset": "Erstelle einen API-Schlüssel im Resend-Dashboard.",
  "settings.inbound.title": "Eingehende E-Mail",
  "settings.inbound.method": "Methode",
  "settings.inbound.methodWebhook": "Webhook (Provider POSTet an /api/email/inbound)",
  "settings.inbound.methodImap": "IMAP (Server fragt Postfach ab)",
  "settings.inbound.methodBoth": "Beide",
  "settings.inbound.webhookSecret": "Webhook-Geheimnis",
  "settings.inbound.webhookSecretPlaceholder": "Starkes Geheimnis wählen",
  "settings.inbound.webhookExplain": "Der Webhook prüft den X-Webhook-Secret-Header gegen diesen Wert.",
  "settings.inbound.webhookUrlOrgHint": "An Organisation {orgId} gebunden. Die Org-ID im Pfad verknüpft den Webhook explizit mit dieser Organisation; der X-Webhook-Secret-Header muss weiterhin übereinstimmen.",
  "settings.outbound.resendWebhookUrlOrgHint": "An Organisation {orgId} gebunden. Die Org-ID im Pfad verknüpft den Webhook explizit mit dieser Organisation; Resends Svix-Signatur wird gegen das Signaturgeheimnis dieser Org verifiziert.",
  "settings.inbound.webhookSecretSet": "Leer lassen, um das bestehende Geheimnis beizubehalten.",
  "settings.inbound.webhookSecretUnset": "Ohne Geheimnis antwortet der Webhook mit 503.",
  "settings.imap.host": "IMAP-Host",
  "settings.imap.port": "IMAP-Port",
  "settings.imap.tls": "TLS verwenden (Port 993)",
  "settings.imap.user": "IMAP-Benutzer",
  "settings.imap.pass": "IMAP-Passwort",
  "settings.imap.passSet": "Passwort bereits hinterlegt. Leer lassen, um es beizubehalten.",
  "settings.imap.passUnset": "Viele Anbieter verlangen ein app-spezifisches Passwort.",
  "settings.imap.folder": "Ordner",
  "settings.imap.pollSeconds": "Abfrageintervall (Sekunden)",
  "settings.imap.pollSecondsHelp": "Min 15s, max 3600s. Standard 120s.",
  "settings.imap.test": "IMAP-Abruf testen",
  "settings.imap.testing": "Abrufen…",
  "settings.imap.testHint": "Führt sofort einen Abruf aus und zeigt, wie viele Nachrichten importiert wurden. Hilfreich, wenn E-Mails ausbleiben.",
  "settings.imap.testOk": "Abruf erfolgreich — {processed} neue Nachricht(en), letzte UID {lastUid} ({ms} ms).",
  "settings.imap.testFailed": "Abruf fehlgeschlagen: {error}",
  "settings.saved": "Einstellungen gespeichert.",
  "settings.noAccess": "Du hast keinen Zugriff auf die Einstellungen.",
  "settings.noAccessRoleLabel": "Aktuelle Rolle: {roles}.",
  "settings.noAccessNoRole": "Bitte einen Administrator, deinem Konto in der DotBots-Plattform eine Rolle zuzuweisen.",
  "settings.noAccessUpgrade": "Bitte einen Administrator, deine Rolle auf Support-Agent oder Administrator zu erweitern.",
  "settings.readOnly": "Deine Rolle ({roles}) erlaubt nur Lesezugriff. Bitte einen Administrator, Änderungen vorzunehmen.",
  "settings.loadFailed": "Einstellungen konnten nicht geladen werden.",

  "settings.inbound.webhookUrl": "Webhook-URL",
  "settings.inbound.payloadHeading": "Erwartetes Payload",
  "settings.inbound.payloadHelp": "Konfiguriere deinen Inbound-Mail-Provider, JSON an diese URL zu POSTen. Sende das obige Geheimnis im X-Webhook-Secret-Header.",
  "settings.outbound.resendWebhookHeading": "Resend-Liefer-Webhook",
  "settings.outbound.resendWebhookHelp": "Trage diese URL in Resend → Webhooks ein. Resend signiert jeden Aufruf mit Svix-Headern (svix-id / svix-timestamp / svix-signature); wir verifizieren sie und aktualisieren den Lieferstatus jeder Nachricht.",
  "settings.outbound.resendWebhookUrl": "Webhook-URL",
  "settings.outbound.resendWebhookSecret": "Resend-Signing-Secret (whsec_…)",
  "settings.outbound.resendWebhookSecretSet": "Ein Signing-Secret ist bereits hinterlegt. Leer lassen, um es beizubehalten.",
  "settings.outbound.resendWebhookSecretUnset": "Kopiere das Signing-Secret, das beim Erstellen des Webhooks in Resend angezeigt wurde.",
  "settings.outbound.resendOnly": "(Wird nur verwendet, wenn Resend als ausgehender Anbieter eingestellt ist.)",

  "settings.tabs.outbound": "Ausgehend",
  "settings.tabs.inbound": "Eingehend",
  "settings.tabs.webhooks": "Webhooks",
  "settings.tabs.templates": "E-Mail-Vorlagen",
  "settings.templates.heading": "Ausgehende E-Mail-Vorlagen",
  "settings.templates.intro": "Bearbeite die transaktionalen Mails, die die App verschickt. Standardvorlagen sind in Englisch, Niederländisch, Französisch und Deutsch enthalten; weitere Sprachen können hinzugefügt werden.",
  "settings.templates.defaultLang": "Standardsprache für ausgehende Mails",
  "settings.templates.defaultLangHelp": "Wird verwendet, wenn die Sprache nicht anders bekannt ist (z. B. Tickets aus eingehenden Mails).",
  "settings.templates.languages": "Sprachen",
  "settings.templates.addLanguage": "Sprache hinzufügen",
  "settings.templates.addLanguagePrompt": "Sprachcode",
  "settings.templates.addLanguageHelp": "Zwei- oder dreistelliger IETF-Code (z. B. it, es, zh-CN). Die KI übersetzt die englischen Standards als Ausgangspunkt; du kannst sie danach bearbeiten.",
  "settings.templates.translateAndAdd": "Übersetzen & hinzufügen",
  "settings.templates.translating": "Übersetze…",
  "settings.templates.removeLangAria": "{lang} entfernen",
  "settings.templates.removeLangConfirm": "Alle Überschreibungen für {lang} entfernen? Eingebaute Sprachen kehren zur Standardvorlage zurück; benutzerdefinierte Sprachen verschwinden aus dem Editor.",
  "settings.templates.langCodeInvalid": "Ungültiger Sprachcode. Zwei oder drei Kleinbuchstaben, optional mit Region (z. B. zh-CN).",
  "settings.templates.langExists": "Diese Sprache ist bereits in der Liste.",
  "settings.templates.langAdded": "Sprache hinzugefügt.",
  "settings.templates.langRemoved": "Sprache entfernt.",
  "settings.templates.kind.label": "E-Mail",
  "settings.templates.kind.confirmation": "Ticketbestätigung",
  "settings.templates.kind.agent_reply": "Antwort des Agents",
  "settings.templates.kind.csat": "Gelöst (CSAT)",
  "settings.templates.kind.reopen_ack": "Wieder geöffnet bestätigt",
  "settings.templates.subject": "Betreff",
  "settings.templates.subjectHelp": "Die Sendelogik fügt [#{ticketNumber}] automatisch voran — hier weglassen.",
  "settings.templates.body": "Text",
  "settings.templates.placeholdersHelp": "Verfügbare Platzhalter: {ticketNumber}, {subject}, {body} (nur Agentenantwort), {csatLink} (nur CSAT-Mail).",
  "settings.templates.resetToDefault": "Auf Standard zurücksetzen",
  "settings.templates.resetConfirm": "Eigene Version verwerfen und zur Standardvorlage zurückkehren?",
  "settings.templates.resetFlash": "Zurückgesetzt.",
  "settings.templates.savedFlash": "Vorlage gespeichert.",
  "settings.templates.previewShow": "Vorschau anzeigen",
  "settings.templates.previewHide": "Vorschau ausblenden",
  "settings.templates.previewHeading": "Vorschau",
  "settings.templates.previewMeta": "Gerendert mit Ticket #{n} und einer Beispielantwort, wird beim Tippen aktualisiert.",
  "settings.templates.previewEmpty": "Text ist leer — tippe etwas in den Editor, um die gerenderte E-Mail hier zu sehen.",
  "settings.templates.defaultUpdated": "Standardsprache aktualisiert.",
  "settings.templates.sourceOverride": "Eigene Version für diese Sprache.",
  "settings.templates.sourceBuiltin": "Eingebaute Standardvorlage. Speichern, um deine Änderungen als Override zu sichern.",
  "settings.templates.sourceFallback": "Keine Vorlage für diese Sprache konfiguriert. Fällt auf die eingebaute {lang}-Vorlage zurück.",
  "settings.summary.outbound": "Ausgehend",
  "settings.summary.inbound": "Eingehend",
  "settings.summary.statusReady": "Bereit",
  "settings.summary.statusIncomplete": "Unvollständig",
  "settings.summary.statusEmpty": "Nicht konfiguriert",
  "settings.payloadShow": "Beispiel-Payload anzeigen",
  "settings.payloadHide": "Beispiel-Payload ausblenden",
  "settings.section.smtpCreds": "SMTP-Anmeldedaten",
  "settings.section.imapServer": "IMAP-Server",
  "settings.section.imapAuth": "IMAP-Anmeldung",
  "settings.section.inboundWebhookHeading": "Webhook für eingehende Mails",
  "settings.section.inboundWebhookHelp": "Konfigurieren Sie Ihren Provider so, dass er Mail-JSON hierher POST.",

  "common.copy": "Kopieren",
  "common.copied": "Kopiert",

  "delivery.queued": "Warteschlange",
  "delivery.sent": "Gesendet",
  "delivery.delivered": "Zugestellt",
  "delivery.opened": "Geöffnet",
  "delivery.delayed": "Verzögert",
  "delivery.bounced": "Abgewiesen",
  "delivery.complained": "Als Spam markiert",
  "delivery.failed": "Fehlgeschlagen",
  "delivery.smtpHint": "SMTP — keine Zustellbestätigung vom Anbieter.",
  "delivery.errorPrefix": "Fehler",

  "common.create": "Erstellen",
  "common.creating": "Erstellen…",
  "common.firstName": "Vorname",
  "common.lastName": "Nachname",
  "common.email": "E-Mail",
  "common.phone": "Telefon",
  "common.required": "erforderlich",

  "contacts.newButton": "Neuer Kontakt",
  "contacts.backToList": "Zurück zu Kontakten",
  "contacts.newHeading": "Neuer Kontakt",
  "contacts.customerType": "Typ",
  "contacts.customerTypeB2B": "Geschäftlich (B2B)",
  "contacts.customerTypeB2C": "Privat (B2C)",
  "contacts.customerTypeVIP": "VIP",
  "contacts.consentEmail": "Kontakt per E-Mail erlauben",
  "contacts.consentMarketing": "Marketing-E-Mails erlauben",
  "contacts.created": "Kontakt erstellt.",
  "contacts.updated": "Kontakt aktualisiert.",
  "contacts.editButton": "Kontakt bearbeiten",
  "contacts.editHeading": "Kontakt bearbeiten",

  "tickets.newButton": "Neues Ticket",
  "tickets.tabMine": "Meine Tickets",
  "tickets.tabAll": "Alle",
  "tickets.tabNoTeam": "Ohne Team",
  "tickets.newHeading": "Neues Ticket",
  "tickets.contactField": "Kontakt",
  "tickets.contactPlaceholder": "Kontakt auswählen…",
  "tickets.contactRequired": "Zuerst einen Kontakt wählen. Falls noch keiner vorhanden ist, lege einen unter Kontakte an.",
  "tickets.bodyField": "Beschreibung",
  "tickets.bodyPlaceholder": "Was möchte der Kunde?",
  "tickets.tagsField": "Tags (kommagetrennt)",
  "tickets.unassigned": "Nicht zugewiesen",
  "tickets.noTeam": "Kein Team",
  "tickets.noSla": "Keine SLA",

  "nav.admin": "Verwaltung",
  "nav.autoArchive": "Auto-Archiv",
  "autoArchive.title": "Auto-Archiv-Regeln",
  "autoArchive.intro": "Eingehende Mails, die auf eine dieser Regeln passen, werden zu einem Ticket und sofort geschlossen — sie bleiben durchsuchbar, fallen aber aus der Warteschlange.",
  "autoArchive.addTitle": "Regel hinzufügen",
  "autoArchive.addButton": "Regel hinzufügen",
  "autoArchive.listTitle": "Bestehende Regeln",
  "autoArchive.empty": "Noch keine Regeln.",
  "autoArchive.noAccess": "Für Auto-Archiv-Regeln braucht man die Admin-Rolle.",
  "autoArchive.kindLabel": "Passt auf",
  "autoArchive.patternLabel": "Muster",
  "autoArchive.noteLabel": "Notiz (optional)",
  "autoArchive.notePlaceholder": "z. B. Stripe-Belege, Instagram-Story-Übersicht",
  "autoArchive.noteFromTicket": "Hinzugefügt aus Ticket {ticketId}",
  "autoArchive.colKind": "Passt auf",
  "autoArchive.colPattern": "Muster",
  "autoArchive.colNote": "Notiz",
  "autoArchive.deleteConfirmTitle": "Regel löschen?",
  "autoArchive.deleteConfirmBody": "Die Auto-Archiv-Regel für '{pattern}' entfernen? Neue passende Mails erscheinen wieder in der Warteschlange.",
  "autoArchive.addFromTicketTitle": "Auto-Archiv-Regel aus diesem Ticket erstellen",
  "autoArchive.addFromTicketShort": "So eine Mail auto-archivieren",
  "autoArchive.addFromTicketBody": "Jede künftige eingehende Mail, die passt, wird als geschlossenes Ticket angelegt.",
  "autoArchive.alsoCloseThisTicket": "Dieses Ticket jetzt auch schließen",
  "autoArchive.kind.from_domain": "Absenderdomäne",
  "autoArchive.kind.from_address": "Absenderadresse (exakt)",
  "autoArchive.kind.subject_contains": "Betreff enthält",
  "autoArchive.help.from_domain": "Passt, wenn die Absenderadresse auf diese Domäne endet (z. B. 'mail.instagram.com' fängt 'x@mail.instagram.com').",
  "autoArchive.help.from_address": "Passt nur auf genau diese Adresse.",
  "autoArchive.help.subject_contains": "Passt, wenn der Betreff diesen Textabschnitt enthält, Groß/Klein unerheblich.",
  "autoArchive.applyToInbox": "Auch auf Tickets anwenden, die bereits in der Inbox liegen",
  "autoArchive.applyToInboxHelp": "Offene Tickets, deren Absender oder Betreff passt, werden ebenfalls geschlossen. Bereits geschlossene Tickets bleiben unberührt.",
  "autoArchive.applyRow": "Auf Inbox anwenden",
  "autoArchive.applying": "Läuft…",
  "autoArchive.sweepResult": "{closed} von {scanned} offenen Tickets geschlossen.",
  "autoArchive.sweepRowResult": "{closed}/{scanned} geschlossen",
  "autoArchive.duplicateError": "Eine Regel für dieses Muster existiert bereits.",
  "admin.title": "Teams & Personen",
  "admin.intro": "Teams, Agents und SLA-Richtlinien dieser Organisation. Teams und Personen aus dem DotBots-Plattform sind entsprechend markiert.",
  "admin.platformBadge": "Plattform",
  "admin.manualBadge": "Manuell",
  "admin.sync": "Mit Plattform synchronisieren",
  "admin.syncing": "Synchronisieren…",
  "admin.syncIntro": "Hole dein Konto und deine Teams aus DotBots Boutique. Weitere Agents werden automatisch beim ersten Öffnen der App übernommen.",
  "admin.syncSuccess": "{count} Team(s) aus der Plattform synchronisiert.",
  "admin.syncSuccessJwt": "Über JWT synchronisiert (Plattform-SDK nicht verfügbar).",

  "admin.teams.heading": "Teams",
  "admin.teams.newButton": "Neues Team",
  "admin.teams.name": "Name",
  "admin.teams.defaultSla": "Standard-SLA",
  "admin.teams.source": "Quelle",
  "admin.teams.deleteConfirm": "Dieses Team löschen? Verbundene Tickets und Agents verlieren die Team-Zuordnung.",

  "admin.agents.heading": "Agents",
  "admin.agents.newButton": "Neuer Agent",
  "admin.agents.team": "Team",
  "admin.agents.platformUserId": "Plattform-Benutzer-ID",
  "admin.agents.deleteConfirm": "Diesen Agent löschen? Seine Tickets verlieren die Zuweisung.",

  "admin.sla.heading": "SLA-Richtlinien",
  "admin.private.heading": "Private Tickets",
  "admin.private.intro": "Offene private Tickets in deiner Organisation. Inhalt und Kontakt bleiben verborgen — Admins können sie nur einer anderen Person zuweisen.",
  "admin.private.reassign": "Zuweisen",
  "admin.private.pickAgent": "Person wählen…",
  "admin.private.empty": "Keine offenen privaten Tickets.",
  "admin.bulk.heading": "Massen-Zuweisung",
  "admin.bulk.intro": "Alle offenen Tickets in einem Klick von einer Warteschlange in eine andere verschieben.",
  "admin.bulk.source": "Von",
  "admin.bulk.target": "Nach",
  "admin.bulk.allPrivate": "Alle privaten Tickets der Organisation",
  "admin.bulk.fromAgent": "Alle Tickets von {name}",
  "admin.bulk.onlyPrivate": "Nur private Tickets",
  "admin.bulk.apply": "Zuweisen",
  "admin.bulk.applying": "Wird zugewiesen…",
  "admin.bulk.success": "{n} Ticket(s) zugewiesen.",
  "admin.forms.heading": "Aufnahmeformulare",
  "admin.forms.intro": "Erzeuge ein öffentliches Ticketformular. Teile die URL oder füge das iframe-Snippet auf deiner Website ein.",
  "admin.forms.newButton": "Neues Formular",
  "admin.forms.newHeading": "Neues Aufnahmeformular",
  "admin.forms.editHeading": "Formular bearbeiten",
  "admin.forms.name": "Formularname",
  "admin.forms.slug": "URL-Slug",
  "admin.forms.slugHelp": "Nur Buchstaben, Ziffern und Bindestriche — Teil der öffentlichen URL.",
  "admin.forms.teamMode": "Team-Zuweisung",
  "admin.forms.modeFixed": "Immer einem Team zuweisen",
  "admin.forms.modeChoose": "Besucher wählt das Team",
  "admin.forms.fixedTeam": "Team",
  "admin.forms.selectableTeams": "Auswählbare Teams",
  "admin.forms.selectableTeamsHelp": "Nichts ankreuzen lässt alle Teams der Organisation zu.",
  "admin.forms.titleField": "Titel (auf dem Formular sichtbar)",
  "admin.forms.titlePlaceholder": "Überschrift, die der Besucher sieht",
  "admin.forms.titleHelp": "Ersetzt den internen Namen des Formulars auf der öffentlichen Seite. Leer lassen, um den Namen weiter anzuzeigen.",
  "admin.forms.introText": "Einleitungstext (optional)",
  "admin.forms.successText": "Bestätigungstext (optional)",
  "admin.forms.redirectUrl": "Weiterleitungs-URL (optional)",
  "admin.forms.redirectUrlPlaceholder": "https://www.beispiel.de/danke",
  "admin.forms.redirectUrlHelp": "Nach erfolgreicher Übermittlung leite Besucher zu dieser URL weiter statt den Bestätigungstext anzuzeigen. Leer lassen = Text anzeigen.",
  "admin.forms.noteHtml": "Kleingedrucktes unter dem Formular (HTML erlaubt)",
  "admin.forms.noteHtmlPlaceholder": "Kein Spam. Eine Bestätigung, eine Erinnerung am Vortag.<br>Gebaut mit <a href=\"#\">SiteAI auf DotBots Boutique</a>.",
  "admin.forms.noteHtmlHelp": "Erscheint als kleiner grauer Absatz unter dem Formular. <br> für Zeilenumbruch, <a href=\"...\">Links</a> erlaubt.",
  "admin.forms.fixedSubject": "Fester Betreff (optional)",
  "admin.forms.fixedSubjectPlaceholder": "z. B. Newsletter-Anmeldung",
  "admin.forms.fixedSubjectHelp": "Legt einen Betreff fest, der für jedes Ticket aus diesem Formular verwendet wird. Das Betreff-Feld ist auf dem öffentlichen Formular ausgeblendet, sobald dies ausgefüllt ist.",
  "admin.forms.fieldLabelsShow": "Feld-Labels & Button anpassen",
  "admin.forms.fieldLabelsHide": "Feld-Labels & Button ausblenden",
  "admin.forms.fieldLabelsHelp": "Überschreibe die sichtbaren Labels und den Text des Senden-Buttons für diese Sprache. Leer lassen, um die KI-Übersetzung beizubehalten.",
  "admin.forms.fieldLabel.name": "Name-Label",
  "admin.forms.fieldLabel.name.placeholder": "z. B. Dein Name",
  "admin.forms.fieldLabel.email": "E-Mail-Label",
  "admin.forms.fieldLabel.email.placeholder": "z. B. E-Mail-Adresse",
  "admin.forms.fieldLabel.subject": "Betreff-Label",
  "admin.forms.fieldLabel.subject.placeholder": "z. B. Betreff",
  "admin.forms.fieldLabel.description": "Beschreibung-Label",
  "admin.forms.fieldLabel.description.placeholder": "z. B. Wie können wir helfen?",
  "admin.forms.fieldLabel.team": "Team-Label",
  "admin.forms.fieldLabel.team.placeholder": "z. B. Abteilung",
  "admin.forms.fieldLabel.teamPlaceholder": "Team-Platzhalter",
  "admin.forms.fieldLabel.teamPlaceholder.placeholder": "z. B. Team wählen…",
  "admin.forms.fieldLabel.submit": "Beschriftung Senden-Button",
  "admin.forms.fieldLabel.submit.placeholder": "z. B. Anfrage senden",
  "admin.forms.publicUrl": "Öffentliche URL",
  "admin.forms.embedJs": "Einbettungs-Code (Skript — empfohlen)",
  "admin.forms.embedJsHelp": "Füge dies auf deiner Website ein.",
  "admin.forms.embedJsRestyleButton": "Restyle-Beispiel ansehen",
  "admin.forms.embedJsRestyleHeading": "Einbettung restylen",
  "admin.forms.embedJsRestyleIntro": "Das Formular verwendet diese Klassennamen. Überschreibe sie in deiner eigenen CSS, um den Stil deiner Seite zu treffen.",
  "admin.forms.embedJsRestyleSample": "Beispiel-CSS — füge dies in dein Stylesheet ein und passe es an:",
  "admin.forms.open": "Öffnen",
  "admin.forms.empty": "Noch keine Aufnahmeformulare.",
  "admin.forms.deleteConfirm": "Formular löschen? Bestehende Tickets bleiben erhalten; die öffentliche URL funktioniert nicht mehr.",
  "admin.forms.translations": "Übersetzungen",
  "admin.forms.addLanguage": "Sprache hinzufügen",
  "admin.forms.pickLanguage": "Sprache wählen…",
  "admin.forms.customLangCode": "oder einen Code eingeben (z. B. zh, pt-BR)",
  "admin.forms.invalidLangCode": "IETF-Sprachcode verwenden: 2-3 Buchstaben, optional eine Region (z. B. en, zh, pt-BR).",
  "admin.forms.noLanguages": "Noch keine Sprachen. Füge eine hinzu, um den Formulartext einzugeben.",
  "admin.forms.translateAll": "Aus {from} in die anderen Sprachen übersetzen",
  "admin.forms.translateAllHint": "Lässt die KI den Intro- und Bestätigungstext dieser Sprache in alle anderen hinzugefügten Sprachen übersetzen.",
  "admin.forms.translating": "Übersetze…",
  "admin.forms.translateEmpty": "Bitte zuerst den Intro- oder Bestätigungstext ausfüllen.",
  "admin.forms.translateNoTargets": "Füge mindestens eine weitere Sprache hinzu, in die übersetzt werden soll.",
  "admin.forms.translateNoSource": "Fülle zuerst eine andere Sprache aus, damit es eine Quelle zum Übersetzen gibt.",
  "admin.forms.fillFrom": "{target} aus {from} ausfüllen",
  "admin.forms.fillFromHint": "Übersetzt den Inhalt aus {from} nur nach {target} — die anderen Sprachen bleiben unberührt.",
  "admin.forms.fillFromDisabled": "{target} ausfüllen (noch keine Quelle)",
  "admin.sla.newButton": "Neue Richtlinie",
  "admin.sla.firstResponseMins": "Erste Antwort (Min.)",
  "admin.sla.resolutionMins": "Lösung (Min.)",
  "admin.sla.deleteConfirm": "Diese SLA-Richtlinie löschen? Tickets und Teams behalten ihre Daten, verlieren aber die Verknüpfung.",

  "admin.detailsToggle": "Details",
  "admin.signature": "E-Mail-Signatur",
  "admin.signaturePlaceholder": "— Max Mustermann\nSupport-Agent",
  "admin.teamSignatureHelp": "Wird verwendet, wenn der Agent keine eigene Signatur hat.",
  "admin.userSignatureHelp": "Wird ausgehenden E-Mails dieses Agents hinzugefügt. Fällt auf die Team-Signatur zurück, wenn leer.",
  "admin.teamAliases": "Team-E-Mail-Adressen",
  "admin.teamAliasesHelp": "Eingehende E-Mails an diese Adressen werden automatisch diesem Team zugeordnet.",
  "admin.userAliases": "Persönliche E-Mail-Adressen",
  "admin.userAliasesHelp": "Eingehende E-Mails an diese Adressen werden diesem Agent zugewiesen und sind für andere Agents unsichtbar.",
};

// Baseline Italian / Spanish translations for the public intake
// form. Anything not present here falls back to English in tr() —
// adding new locales is just a matter of dropping in another block
// and registering it below.
const it: Dict = {
  "common.loading": "Caricamento…",
  "common.cancel": "Annulla",
  "common.add": "Aggiungi",
  "intake.name": "Nome",
  "intake.email": "Email",
  "intake.subject": "Oggetto",
  "intake.description": "Descrizione",
  "intake.submit": "Crea ticket",
  "intake.submitting": "Invio…",
  "intake.captchaRequired": "Completa il captcha per continuare.",
  "intake.teamRequired": "Seleziona un team.",
  "intake.teamPlaceholder": "Scegli un team…",
  "intake.protectedBy": "Protetto da Cloudflare Turnstile.",
  "intake.thanksHeading": "Grazie!",
  "intake.thanksDefault": "Il tuo ticket #{n} è stato creato.",
  "intake.thanksReplyHint": "Riceverai un'email di conferma. Rispondi direttamente a quell'email per aggiungere informazioni.",
  "intake.unavailableHeading": "Modulo temporaneamente non disponibile",
  "intake.captchaMissing": "Il modulo non è protetto dagli abusi e non può essere mostrato. L'amministratore deve configurare TURNSTILE_SITE_KEY.",
  "intake.errCaptcha": "Verifica captcha fallita. Risolvi nuovamente la sfida e invia di nuovo.",
  "intake.errFormNotFound": "Questo modulo non è più disponibile. Chiedi un link aggiornato al tuo contatto.",
  "intake.errValidation": "Alcuni campi sono mancanti o non validi. Controlla e riprova.",
  "intake.errRateLimit": "Troppi invii da questo indirizzo. Riprova più tardi.",
  "intake.errGeneric": "Qualcosa è andato storto. Riprova più tardi.",
  "ticket.team": "Team",
};

const es: Dict = {
  "common.loading": "Cargando…",
  "common.cancel": "Cancelar",
  "common.add": "Añadir",
  "intake.name": "Nombre",
  "intake.email": "Correo electrónico",
  "intake.subject": "Asunto",
  "intake.description": "Descripción",
  "intake.submit": "Crear ticket",
  "intake.submitting": "Enviando…",
  "intake.captchaRequired": "Completa el captcha para continuar.",
  "intake.teamRequired": "Selecciona un equipo.",
  "intake.teamPlaceholder": "Elige un equipo…",
  "intake.protectedBy": "Protegido por Cloudflare Turnstile.",
  "intake.thanksHeading": "¡Gracias!",
  "intake.thanksDefault": "Tu ticket #{n} ha sido creado.",
  "intake.thanksReplyHint": "Recibirás un correo de confirmación. Responde directamente a ese correo para añadir información.",
  "intake.unavailableHeading": "Formulario temporalmente no disponible",
  "intake.captchaMissing": "El formulario no está protegido contra abusos y no se puede mostrar. El administrador debe configurar TURNSTILE_SITE_KEY.",
  "intake.errCaptcha": "La verificación del captcha falló. Resuelve el desafío nuevamente y reenvía.",
  "intake.errFormNotFound": "Este formulario ya no está disponible. Pide a tu contacto un enlace actualizado.",
  "intake.errValidation": "Algunos campos faltan o son inválidos. Verifica y vuelve a intentar.",
  "intake.errRateLimit": "Demasiados envíos desde esta dirección. Inténtalo más tarde.",
  "intake.errGeneric": "Algo salió mal. Inténtalo más tarde.",
  "ticket.team": "Equipo",
};

// Use a string-keyed map so additional locales can be added without
// widening the Lang union — tr() does the lookup with English
// fallback, so a missing key just shows the English string.
export const TRANSLATIONS: Record<string, Dict> = { en, nl, fr, de, it, es };
